import { pickCopy } from '@/lib/mycio/translations'
import { getMyCIOLanguage, type MyCIOLanguageCode } from '@/lib/mycio/language'

/**
 * The chat's own panels and block chrome: the durable full-review panel (ReviewJobPanel /
 * reviewPolling.ts), the case-questions panel and its report (CaseQuestions / casePolling.ts),
 * the plan-report button (GeneratePlanReport), the personal agenda (PersonalAgenda), the chooser
 * and receipt labels in blocks.tsx, and MyCIOChat's local fallbacks.
 *
 * Rules of the house (identical to planViewExtras.ts / planWidgets.ts):
 * - `en` is the English these components shipped with. Two deliberate English changes: a raw
 *   `failure_class`, stage key or section key is never shown any more — each has a label here
 *   (unknown codes read as a generic label, never the code) — and a figure receipt names "the
 *   planning engine" instead of the tool id, which moves to a tooltip.
 * - Numbers are formatted by the caller with the member's number locale and passed in as text;
 *   a count is passed as well only where a language needs it to choose a plural.
 * - Register: computed, not advice. Same person/register as copy/assistant.ts per language.
 * - zh-Hant is derived from `zh` by pickCopy's opencc chokepoint; only `zh` is authored.
 */

/** Native-speaker review status of the non-English copy in this module. */
export const NATIVE_REVIEW = {
  reviewed: false,
  authored: '2026-09-21 in-session',
  note: 'every non-English entry: review.*, sections.*, sectionTitles.*, caseQ.*, report.*, generate.*, agenda.*, blocks.*, chat.*',
} as const

/** Worker failure classes the review queue records (review_worker.py / review_queue / the status proxy). */
export const REVIEW_FAILURE_CLASSES = [
  'internal_error', 'engine_error', 'engine_refused', 'engine_busy', 'engine_deadline', 'persistence_failed',
  'lease_expired', 'cancelled', 'review_status_unavailable', 'review_access_unavailable',
] as const
export type ReviewFailureClass = (typeof REVIEW_FAILURE_CLASSES)[number]

/** Section keys a case report or a working draft can carry (assembly_requirements.py + the narrative keys). */
export const REPORT_SECTION_KEYS = [
  'engagement_scope', 'assumptions', 'executive_summary', 'current_condition', 'tax_position', 'swot',
  'recommendations_and_options', 'implementation_actions', 'monitoring_and_review', 'professionals',
  'sections_not_provided', 'open_items',
  'executive_letter', 'overview', 'what_you_asked', 'situation', 'goals', 'personal_and_goals', 'cashflow_debt',
  'retirement', 'education', 'protection', 'tax', 'investments', 'work_business', 'estate', 'dates_that_matter',
  'risk', 'tradeoffs', 'next_steps', 'monitoring', 'net_worth_statement', 'cash_flow_and_income', 'balance_sheet',
  'judgement_and_options', 'actions_and_administration', 'tax_and_sources', 'cross_border_tax', 'tax_evaluation',
] as const
export type ReportSectionKey = (typeof REPORT_SECTION_KEYS)[number]

/**
 * The required-section TITLES the backend sends in `unavailable_sections` (English, from
 * assembly_requirements.REQUIRED_SECTIONS), keyed back to their section key so they render in the
 * member's language. Kept in step with the Python table by chatPanels.test.ts.
 */
export const REQUIRED_SECTION_TITLES_EN: Record<string, ReportSectionKey> = {
  'What you asked and what this plan covers': 'engagement_scope',
  'The assumptions behind the figures': 'assumptions',
  'A one-page summary': 'executive_summary',
  'Where you stand today': 'current_condition',
  'Where tax changes the answer': 'tax_position',
  'Material risks and resilience': 'swot',
  'The options open to you': 'recommendations_and_options',
  'What happens next, and who does it': 'implementation_actions',
  'When this plan is reviewed again': 'monitoring_and_review',
  'Other professionals this plan needs': 'professionals',
}

export type MyCIOChatPanelsCopy = {
  review: {
    intro: string
    queueButton: string
    queuing: string
    checking: string
    queued: string
    /** `n` is the formatted count; `count` picks the plural. */
    ahead: (count: number, n: string) => string
    estimatedStart: (eta: string) => string
    waitingWorker: string
    provisionalTiming: string
    stageProgress: (stage: string, done: string, total: string) => string
    stageOngoing: (stage: string) => string
    estimatedCompletion: (eta: string) => string
    workerUnresponsive: string
    draftReady: string
    draftReadySub: string
    complete: string
    savedVersioned: string
    letterPending: string
    letterFailed: string
    letterUnverified: string
    letterIncomplete: string
    noReference: string
    needsInput: string
    needsInputSub: string
    superseded: string
    failed: string
    reason: (label: string) => string
    cancelled: string
    none: string
    accessUnavailable: string
    /** a single time rather than a range */
    etaAbout: (time: string) => string
    failureReasons: Record<ReviewFailureClass | 'other', string>
    stages: { load_snapshot: string; compute_review: string; publish_plan: string; needs_input: string; computing: string; working: string }
    retrievalError: string
    retryLoading: string
    notUpdated: string
    notCompleted: string
    questionsYouCanAnswer: string
    runNow: string
    cancel: string
    reviewAgain: string
    dismiss: string
  }
  /** Section headings of a case report or working draft, by section key. */
  sections: Record<ReportSectionKey, string>
  caseQ: {
    requestFailed: string
    refreshFailed: string
    answerFailed: string
    failed: string
    reading: string
    currencyFormat: string
    currencyFailed: string
    planningCurrencyAria: string
    useCurrency: string
    analyzing: string
    introChoose: string
    introFacts: string
    statement: (n: string) => string
    person: (n: string) => string
    /** lower-case form used inside `nameFor` */
    personInline: (n: string) => string
    nameFor: (who: string) => string
    saveNames: string
    currencyCodeLabel: string
    currencyPlaceholder: string
    caseSensitive: string
    replyInChat: string
    needsFactUpdate: string
    alsoWorking: string
    currencyQuestion: string
  }
  report: { complete: string; partial: string; notComplete: string }
  generate: {
    signIn: string
    unreachable: string
    couldNotStart: string
    button: string
    working: string
    notProduced: string
    questionsWaiting: (count: number, n: string) => string
  }
  agenda: {
    status: { awaiting: string; open: string; failed: string; answered: string }
    clarifyTitle: string
    clarifyBody: string
    agendaTitle: string
    agendaLead: string
    openCount: (count: number, n: string) => string
    allAnswered: string
    mismatchTitle: string
    /** rendered after the bold subject */
    mismatchLine: (described: string, profile: string) => string
  }
  blocks: {
    questionN: (n: string) => string
    sendNofM: (chosen: string, total: string) => string
    answerAllFirst: string
    back: string
    next: string
    typeOwn: string
    stepOfTotal: (n: string, total: string) => string
    figuresSource: string
    computedByEngine: string
    answerAria: string
    yourAnswer: string
    send: string
    /** percentage points after a signed, formatted number */
    pp: (n: string) => string
  }
  chat: {
    noted: string
    couldNotRecord: string
    couldNotRecordRetry: string
    replyingTo: string
    cancelReply: string
  }
}

// Plural helpers for the languages whose count words change form.
const ruPlural = (count: number, one: string, few: string, many: string) => {
  const m10 = count % 10
  const m100 = count % 100
  if (m10 === 1 && m100 !== 11) return one
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return few
  return many
}

const en: MyCIOChatPanelsCopy = {
  review: {
    intro: 'Run a full review of your plan on your current inputs — it queues, computes in the background, and saves a new plan version.',
    queueButton: 'Queue a full review',
    queuing: 'Queuing…',
    checking: 'Checking your review…',
    queued: 'Your updated review is queued.',
    ahead: (count, n) => `${n} review${count === 1 ? ' is' : 's are'} ahead of you.`,
    estimatedStart: (eta) => `Estimated start: ${eta}.`,
    waitingWorker: 'Current stage: waiting for a compute worker.',
    provisionalTiming: 'This timing is an estimate and may change.',
    stageProgress: (stage, done, total) => `${stage} — ${done} of ${total} stages complete.`,
    stageOngoing: (stage) => `${stage}…`,
    estimatedCompletion: (eta) => `Estimated completion: ${eta}.`,
    workerUnresponsive: 'The compute worker has not reported recently. Recovery requires an available worker; completion timing is unknown.',
    draftReady: 'Your working review is ready.',
    draftReadySub: 'Completed analysis is available. The saved plan has not been updated.',
    complete: 'Your review is complete.',
    savedVersioned: 'The updated plan is saved and versioned.',
    letterPending: 'Its letter is still being written.',
    letterFailed: 'The plan was saved, but its letter is unavailable. You can still view the saved results.',
    letterUnverified: "The letter's status for this version could not be confirmed; the figures stand.",
    letterIncomplete: 'Its letter is missing one or more sections — the plan page names them; the figures stand.',
    noReference: 'The review finished, but its result reference is unavailable.',
    needsInput: 'Your review could not run yet — it needs an answer from you first.',
    needsInputSub: 'Answer in chat or on your plan, then ask for the review again.',
    superseded: 'Your inputs changed — a newer review covers the current numbers.',
    failed: 'This review could not complete.',
    reason: (label) => `Reason: ${label}.`,
    cancelled: 'This review was cancelled.',
    none: 'No review has been requested yet.',
    accessUnavailable: 'Unable to access this review. Check your sign-in and access.',
    etaAbout: (time) => `about ${time}`,
    failureReasons: {
      internal_error: 'an internal error on our side',
      engine_error: 'the plan calculation returned an error',
      engine_refused: 'the plan calculation could not accept these inputs',
      engine_busy: 'the plan calculation was busy',
      engine_deadline: 'the calculation ran out of time',
      persistence_failed: 'the result could not be saved',
      lease_expired: 'the compute worker stopped responding',
      cancelled: 'the review was cancelled',
      review_status_unavailable: 'the review status could not be read',
      review_access_unavailable: 'this review could not be accessed',
      other: 'an unexpected error',
    },
    stages: {
      load_snapshot: 'Loading your frozen inputs',
      compute_review: 'Running the Monte Carlo simulation',
      publish_plan: 'Saving your updated plan version',
      needs_input: 'More information needed before this plan can compute',
      computing: 'Computing',
      working: 'Working',
    },
    retrievalError: 'Your review finished, but its results could not be loaded. Retrying delivery without running the calculation again.',
    retryLoading: 'Retry loading',
    notUpdated: 'The saved plan has not been updated.',
    notCompleted: 'Not completed this run',
    questionsYouCanAnswer: 'Questions you can answer',
    runNow: 'Run it now',
    cancel: 'Cancel',
    reviewAgain: 'Review again',
    dismiss: 'Dismiss',
  },
  sections: {
    engagement_scope: 'What you asked and what this plan covers',
    assumptions: 'The assumptions behind the figures',
    executive_summary: 'A one-page summary',
    current_condition: 'Where you stand today',
    tax_position: 'Where tax changes the answer',
    swot: 'Material risks and resilience',
    recommendations_and_options: 'The options open to you',
    implementation_actions: 'What happens next, and who does it',
    monitoring_and_review: 'When this plan is reviewed again',
    professionals: 'Other professionals this plan needs',
    sections_not_provided: 'Sections not provided',
    open_items: 'Open items',
    executive_letter: 'Your plan now',
    overview: 'Overview',
    what_you_asked: 'What you asked',
    situation: 'Your situation',
    goals: 'Goals',
    personal_and_goals: 'You and your goals',
    cashflow_debt: 'Cash flow & debt',
    retirement: 'Retirement',
    education: 'Education',
    protection: 'Protection',
    tax: 'Tax',
    investments: 'Investments',
    work_business: 'Work & business',
    estate: 'Estate',
    dates_that_matter: 'Dates that matter',
    risk: 'Risks and resilience',
    tradeoffs: 'Your options',
    next_steps: 'What to do next',
    monitoring: 'Implementation and monitoring',
    net_worth_statement: 'Net worth statement',
    cash_flow_and_income: 'Cash flow and income',
    balance_sheet: 'Balance sheet',
    judgement_and_options: 'Judgement and options',
    actions_and_administration: 'Actions and administration',
    tax_and_sources: 'Tax and sources',
    cross_border_tax: 'Cross-border tax',
    tax_evaluation: 'Tax evaluation',
  },
  caseQ: {
    requestFailed: 'The request could not be completed. Check your answer and try again.',
    refreshFailed: 'Unable to refresh your plan right now. Retrying…',
    answerFailed: 'That answer could not be recorded. Please try again.',
    failed: 'I couldn’t finish reviewing what you sent. Please try the review again.',
    reading: 'Reading what you sent…',
    currencyFormat: 'Enter a three-letter currency code, such as USD, GBP or EUR.',
    currencyFailed: 'That currency could not be recorded.',
    planningCurrencyAria: 'Planning currency',
    useCurrency: 'Use this currency',
    analyzing: 'Working through your planning questions…',
    introChoose: 'These questions help clarify your figures. Each answer updates the part of your plan that depends on it.',
    introFacts: 'A few things your plan depends on aren’t stated anywhere in what you sent, and I won’t guess at them.',
    statement: (n) => `Statement ${n}:`,
    person: (n) => `Person ${n}`,
    personInline: (n) => `person ${n}`,
    nameFor: (who) => `Name for ${who}`,
    saveNames: 'Save names',
    currencyCodeLabel: 'Currency code',
    currencyPlaceholder: 'e.g. USD',
    caseSensitive: 'Codes are case-sensitive: GBP is pounds, GBp is pence.',
    replyInChat: 'Reply to this question in chat',
    needsFactUpdate: 'This question needs a fact update before it can be settled.',
    alsoWorking: 'I’m also working through the rest of your planning questions.',
    currencyQuestion: 'Which currency do you want your plan to use for its totals?',
  },
  report: {
    complete: 'Your case review',
    partial: 'What I can establish so far',
    notComplete: 'This is not a complete financial plan. Open items are shown rather than guessed.',
  },
  generate: {
    signIn: 'Sign in to generate your plan report.',
    unreachable: 'The plan service is unreachable — try again.',
    couldNotStart: 'Could not start the report — try again shortly.',
    button: 'Generate my plan report',
    working: 'Working through your plan — computing each section, and naming what any section is still waiting on. This usually takes a minute or two.',
    notProduced: 'The report could not be produced. Nothing was guessed in its place — please try again, and if this repeats, the failure is ours to fix, not yours.',
    questionsWaiting: (_count, n) => `${n} question(s) from your case are waiting in the assistant — answering them unblocks the sections that depend on them.`,
  },
  agenda: {
    status: { awaiting: 'waiting on you', open: 'still open', failed: 'ours to fix', answered: 'answered' },
    clarifyTitle: 'Clarify your profile',
    clarifyBody: 'These come from your saved profile, not from this report. Fixing them upgrades every plan and report that reads it.',
    agendaTitle: 'Your agenda',
    agendaLead: 'What this report worked through on your situation',
    openCount: (count, n) => ` — ${n} item${count === 1 ? '' : 's'} still open.`,
    allAnswered: ' — every item is answered.',
    mismatchTitle: 'This doesn’t match your saved profile',
    mismatchLine: (described, profile) => `: you described ${described}; your profile says ${profile}.`,
  },
  blocks: {
    questionN: (n) => `Question ${n}`,
    sendNofM: (chosen, total) => `Send ${chosen} of ${total}`,
    answerAllFirst: 'You can answer them all before sending.',
    back: 'Back',
    next: 'Next',
    typeOwn: 'Or type your own answer',
    stepOfTotal: (n, total) => `${n} of ${total}`,
    figuresSource: 'Where these numbers come from',
    computedByEngine: 'computed by MyCIO from your plan',
    answerAria: 'Answer',
    yourAnswer: 'Your answer',
    send: 'Send',
    pp: (n) => `${n}pp`,
  },
  chat: {
    noted: 'Noted.',
    couldNotRecord: 'I couldn\'t record that just now.',
    couldNotRecordRetry: 'I couldn\'t record that just now — please try again.',
    replyingTo: 'Replying to:',
    cancelReply: 'Cancel reply',
  },
}

const es: MyCIOChatPanelsCopy = {
  review: {
    intro: 'Haz una revisión completa de tu plan con tus datos actuales: entra en cola, se calcula en segundo plano y guarda una nueva versión del plan.',
    queueButton: 'Poner en cola una revisión completa',
    queuing: 'Poniendo en cola…',
    checking: 'Consultando tu revisión…',
    queued: 'Tu revisión actualizada está en cola.',
    ahead: (count, n) => (count === 1 ? `Hay ${n} revisión por delante de la tuya.` : `Hay ${n} revisiones por delante de la tuya.`),
    estimatedStart: (eta) => `Inicio estimado: ${eta}.`,
    waitingWorker: 'Etapa actual: esperando un servidor de cálculo.',
    provisionalTiming: 'Este momento es una estimación y puede cambiar.',
    stageProgress: (stage, done, total) => `${stage} — ${done} de ${total} etapas completadas.`,
    stageOngoing: (stage) => `${stage}…`,
    estimatedCompletion: (eta) => `Finalización estimada: ${eta}.`,
    workerUnresponsive: 'El servidor de cálculo no ha informado recientemente. Para reanudar hace falta un servidor disponible; no se sabe cuándo terminará.',
    draftReady: 'Tu revisión de trabajo está lista.',
    draftReadySub: 'El análisis completado está disponible. El plan guardado no se ha actualizado.',
    complete: 'Tu revisión ha finalizado.',
    savedVersioned: 'El plan actualizado está guardado y versionado.',
    letterPending: 'Su carta todavía se está redactando.',
    letterFailed: 'El plan se guardó, pero su carta no está disponible. Puedes consultar igualmente los resultados guardados.',
    letterUnverified: 'No se pudo confirmar el estado de la carta de esta versión; las cifras se mantienen.',
    letterIncomplete: 'A su carta le faltan una o más secciones — la página del plan las indica; las cifras se mantienen.',
    noReference: 'La revisión terminó, pero la referencia de su resultado no está disponible.',
    needsInput: 'Tu revisión aún no ha podido ejecutarse: primero necesita una respuesta tuya.',
    needsInputSub: 'Responde en el chat o en tu plan y vuelve a solicitar la revisión.',
    superseded: 'Tus datos cambiaron: una revisión más reciente cubre las cifras actuales.',
    failed: 'Esta revisión no pudo completarse.',
    reason: (label) => `Motivo: ${label}.`,
    cancelled: 'Esta revisión se canceló.',
    none: 'Todavía no se ha solicitado ninguna revisión.',
    accessUnavailable: 'No se puede acceder a esta revisión. Comprueba tu inicio de sesión y tus permisos.',
    etaAbout: (time) => `hacia las ${time}`,
    failureReasons: {
      internal_error: 'un error interno por nuestra parte',
      engine_error: 'el motor de planificación devolvió un error',
      engine_refused: 'el motor de planificación no pudo aceptar estos datos',
      engine_busy: 'el motor de planificación estaba ocupado',
      engine_deadline: 'el cálculo superó el tiempo disponible',
      persistence_failed: 'no se pudo guardar el resultado',
      lease_expired: 'el servidor de cálculo dejó de responder',
      cancelled: 'la revisión se canceló',
      review_status_unavailable: 'no se pudo leer el estado de la revisión',
      review_access_unavailable: 'no se pudo acceder a esta revisión',
      other: 'un error inesperado',
    },
    stages: {
      load_snapshot: 'Cargando tus datos congelados',
      compute_review: 'Ejecutando la simulación de Monte Carlo',
      publish_plan: 'Guardando la nueva versión de tu plan',
      needs_input: 'Se necesita más información para poder calcular este plan',
      computing: 'Calculando',
      working: 'Procesando',
    },
    retrievalError: 'Tu revisión terminó, pero sus resultados no se pudieron cargar. Reintentando la entrega sin volver a ejecutar el cálculo.',
    retryLoading: 'Reintentar la carga',
    notUpdated: 'El plan guardado no se ha actualizado.',
    notCompleted: 'No completado en esta ejecución',
    questionsYouCanAnswer: 'Preguntas que puedes responder',
    runNow: 'Ejecutar ahora',
    cancel: 'Cancelar',
    reviewAgain: 'Revisar de nuevo',
    dismiss: 'Descartar',
  },
  sections: {
    engagement_scope: 'Lo que preguntaste y lo que cubre este plan',
    assumptions: 'Los supuestos detrás de las cifras',
    executive_summary: 'Resumen en una página',
    current_condition: 'Tu situación actual',
    tax_position: 'Dónde los impuestos cambian la respuesta',
    swot: 'Riesgos relevantes y resiliencia',
    recommendations_and_options: 'Las opciones a tu alcance',
    implementation_actions: 'Próximos pasos y quién los da',
    monitoring_and_review: 'Cuándo se vuelve a revisar este plan',
    professionals: 'Otros profesionales que necesita este plan',
    sections_not_provided: 'Secciones no aportadas',
    open_items: 'Puntos abiertos',
    executive_letter: 'Tu plan hoy',
    overview: 'Visión general',
    what_you_asked: 'Lo que preguntaste',
    situation: 'Tu situación',
    goals: 'Objetivos',
    personal_and_goals: 'Tú y tus objetivos',
    cashflow_debt: 'Flujo de caja y deuda',
    retirement: 'Jubilación',
    education: 'Educación',
    protection: 'Protección',
    tax: 'Impuestos',
    investments: 'Inversiones',
    work_business: 'Trabajo y empresa',
    estate: 'Sucesión',
    dates_that_matter: 'Fechas clave',
    risk: 'Riesgos y resiliencia',
    tradeoffs: 'Tus opciones',
    next_steps: 'Qué hacer a continuación',
    monitoring: 'Implementación y seguimiento',
    net_worth_statement: 'Estado de patrimonio neto',
    cash_flow_and_income: 'Flujo de caja e ingresos',
    balance_sheet: 'Balance patrimonial',
    judgement_and_options: 'Criterio y opciones',
    actions_and_administration: 'Acciones y gestión',
    tax_and_sources: 'Impuestos y fuentes',
    cross_border_tax: 'Fiscalidad transfronteriza',
    tax_evaluation: 'Evaluación fiscal',
  },
  caseQ: {
    requestFailed: 'No se pudo completar la solicitud. Revisa tu respuesta e inténtalo de nuevo.',
    refreshFailed: 'Ahora mismo no se puede actualizar tu plan. Reintentando…',
    answerFailed: 'No se pudo registrar esa respuesta. Inténtalo de nuevo.',
    failed: 'No pude terminar de revisar lo que enviaste. Vuelve a intentar la revisión.',
    reading: 'Leyendo lo que enviaste…',
    currencyFormat: 'Introduce un código de divisa de tres letras, como USD, GBP o EUR.',
    currencyFailed: 'No se pudo registrar esa divisa.',
    planningCurrencyAria: 'Divisa del plan',
    useCurrency: 'Usar esta divisa',
    analyzing: 'Analizando tus preguntas de planificación…',
    introChoose: 'Estas preguntas ayudan a precisar tus cifras. Cada respuesta actualiza la parte del plan que depende de ella.',
    introFacts: 'Algunos datos de los que depende tu plan no aparecen en lo que enviaste, y no voy a suponerlos.',
    statement: (n) => `Declaración ${n}:`,
    person: (n) => `Persona ${n}`,
    personInline: (n) => `persona ${n}`,
    nameFor: (who) => `Nombre de ${who}`,
    saveNames: 'Guardar nombres',
    currencyCodeLabel: 'Código de divisa',
    currencyPlaceholder: 'p. ej. USD',
    caseSensitive: 'Los códigos distinguen mayúsculas: GBP son libras, GBp son peniques.',
    replyInChat: 'Responder a esta pregunta en el chat',
    needsFactUpdate: 'Esta pregunta necesita que se actualice un dato antes de poder resolverse.',
    alsoWorking: 'También estoy analizando el resto de tus preguntas de planificación.',
    currencyQuestion: '¿En qué divisa debe expresar tu plan sus totales?',
  },
  report: {
    complete: 'Revisión de tu caso',
    partial: 'Lo que puedo establecer hasta ahora',
    notComplete: 'Esto no es un plan financiero completo. Los puntos abiertos se muestran en lugar de suponerse.',
  },
  generate: {
    signIn: 'Inicia sesión para generar el informe de tu plan.',
    unreachable: 'El servicio del plan no responde — inténtalo de nuevo.',
    couldNotStart: 'No se pudo iniciar el informe — inténtalo de nuevo en breve.',
    button: 'Generar el informe de mi plan',
    working: 'Analizando tu plan: calculando cada sección e indicando qué le falta a cada una. Suele tardar uno o dos minutos.',
    notProduced: 'No se pudo generar el informe. No se ha supuesto nada en su lugar: inténtalo de nuevo y, si se repite, el fallo es nuestro, no tuyo.',
    questionsWaiting: (count, n) => (count === 1
      ? `${n} pregunta de tu caso te espera en el asistente; responderla desbloquea las secciones que dependen de ella.`
      : `${n} preguntas de tu caso te esperan en el asistente; responderlas desbloquea las secciones que dependen de ellas.`),
  },
  agenda: {
    status: { awaiting: 'pendiente de ti', open: 'aún abierto', failed: 'nos toca resolverlo', answered: 'respondido' },
    clarifyTitle: 'Aclara tu perfil',
    clarifyBody: 'Esto procede de tu perfil guardado, no de este informe. Corregirlo mejora cada plan e informe que lo utiliza.',
    agendaTitle: 'Tu agenda',
    agendaLead: 'Lo que este informe analizó sobre tu situación',
    openCount: (count, n) => (count === 1 ? ` — ${n} punto sigue abierto.` : ` — ${n} puntos siguen abiertos.`),
    allAnswered: ' — todos los puntos están respondidos.',
    mismatchTitle: 'Esto no coincide con tu perfil guardado',
    mismatchLine: (described, profile) => `: describiste ${described}; tu perfil indica ${profile}.`,
  },
  blocks: {
    questionN: (n) => `Pregunta ${n}`,
    sendNofM: (chosen, total) => `Enviar ${chosen} de ${total}`,
    answerAllFirst: 'Puedes responderlas todas antes de enviar.',
    back: 'Atrás',
    next: 'Siguiente',
    typeOwn: 'O escribe tu propia respuesta',
    stepOfTotal: (n, total) => `${n} de ${total}`,
    figuresSource: 'De dónde salen estas cifras',
    computedByEngine: 'calculado por el motor de planificación',
    answerAria: 'Respuesta',
    yourAnswer: 'Tu respuesta',
    send: 'Enviar',
    pp: (n) => `${n} p. p.`,
  },
  chat: {
    noted: 'Anotado.',
    couldNotRecord: 'No he podido registrarlo en este momento.',
    couldNotRecordRetry: 'No he podido registrarlo en este momento — inténtalo de nuevo.',
    replyingTo: 'Respondiendo a:',
    cancelReply: 'Cancelar respuesta',
  },
}

const pt: MyCIOChatPanelsCopy = {
  review: {
    intro: 'Faça uma revisão completa do seu plano com os dados atuais — ela entra na fila, é calculada em segundo plano e salva uma nova versão do plano.',
    queueButton: 'Colocar uma revisão completa na fila',
    queuing: 'Colocando na fila…',
    checking: 'Verificando a sua revisão…',
    queued: 'A sua revisão atualizada está na fila.',
    ahead: (count, n) => (count === 1 ? `Há ${n} revisão à frente da sua.` : `Há ${n} revisões à frente da sua.`),
    estimatedStart: (eta) => `Início estimado: ${eta}.`,
    waitingWorker: 'Etapa atual: aguardando um servidor de cálculo.',
    provisionalTiming: 'Este momento é uma estimativa e pode mudar.',
    stageProgress: (stage, done, total) => `${stage} — ${done} de ${total} etapas concluídas.`,
    stageOngoing: (stage) => `${stage}…`,
    estimatedCompletion: (eta) => `Conclusão estimada: ${eta}.`,
    workerUnresponsive: 'O servidor de cálculo não se reportou recentemente. A retomada depende de um servidor disponível; o prazo de conclusão é desconhecido.',
    draftReady: 'A sua revisão de trabalho está pronta.',
    draftReadySub: 'A análise concluída está disponível. O plano salvo não foi atualizado.',
    complete: 'A sua revisão foi concluída.',
    savedVersioned: 'O plano atualizado está salvo e versionado.',
    letterPending: 'A carta ainda está sendo redigida.',
    letterFailed: 'O plano foi salvo, mas a carta não está disponível. Você ainda pode ver os resultados salvos.',
    letterUnverified: 'Não foi possível confirmar o estado da carta desta versão; os valores se mantêm.',
    letterIncomplete: 'Falta uma ou mais seções na carta — a página do plano as indica; os valores se mantêm.',
    noReference: 'A revisão terminou, mas a referência do resultado não está disponível.',
    needsInput: 'A sua revisão ainda não pôde ser executada — primeiro ela precisa de uma resposta sua.',
    needsInputSub: 'Responda no chat ou no seu plano e depois solicite a revisão novamente.',
    superseded: 'Os seus dados mudaram — uma revisão mais recente cobre os valores atuais.',
    failed: 'Esta revisão não pôde ser concluída.',
    reason: (label) => `Motivo: ${label}.`,
    cancelled: 'Esta revisão foi cancelada.',
    none: 'Nenhuma revisão foi solicitada ainda.',
    accessUnavailable: 'Não foi possível acessar esta revisão. Verifique o seu login e as suas permissões.',
    etaAbout: (time) => `por volta das ${time}`,
    failureReasons: {
      internal_error: 'um erro interno do nosso lado',
      engine_error: 'o motor de planejamento retornou um erro',
      engine_refused: 'o motor de planejamento não pôde aceitar estes dados',
      engine_busy: 'o motor de planejamento estava ocupado',
      engine_deadline: 'o cálculo excedeu o tempo disponível',
      persistence_failed: 'não foi possível salvar o resultado',
      lease_expired: 'o servidor de cálculo parou de responder',
      cancelled: 'a revisão foi cancelada',
      review_status_unavailable: 'não foi possível ler o estado da revisão',
      review_access_unavailable: 'não foi possível acessar esta revisão',
      other: 'um erro inesperado',
    },
    stages: {
      load_snapshot: 'Carregando os seus dados congelados',
      compute_review: 'Executando a simulação de Monte Carlo',
      publish_plan: 'Salvando a nova versão do seu plano',
      needs_input: 'São necessárias mais informações para calcular este plano',
      computing: 'Calculando',
      working: 'Processando',
    },
    retrievalError: 'A sua revisão terminou, mas os resultados não puderam ser carregados. Tentando entregar de novo sem refazer o cálculo.',
    retryLoading: 'Tentar carregar de novo',
    notUpdated: 'O plano salvo não foi atualizado.',
    notCompleted: 'Não concluído nesta execução',
    questionsYouCanAnswer: 'Perguntas que você pode responder',
    runNow: 'Executar agora',
    cancel: 'Cancelar',
    reviewAgain: 'Revisar novamente',
    dismiss: 'Dispensar',
  },
  sections: {
    engagement_scope: 'O que você perguntou e o que este plano abrange',
    assumptions: 'As premissas por trás dos valores',
    executive_summary: 'Resumo de uma página',
    current_condition: 'A sua situação hoje',
    tax_position: 'Onde os impostos mudam a resposta',
    swot: 'Riscos relevantes e resiliência',
    recommendations_and_options: 'As opções ao seu alcance',
    implementation_actions: 'Próximos passos e quem os executa',
    monitoring_and_review: 'Quando este plano será revisado novamente',
    professionals: 'Outros profissionais de que este plano precisa',
    sections_not_provided: 'Seções não fornecidas',
    open_items: 'Pontos em aberto',
    executive_letter: 'O seu plano hoje',
    overview: 'Visão geral',
    what_you_asked: 'O que você perguntou',
    situation: 'A sua situação',
    goals: 'Objetivos',
    personal_and_goals: 'Você e os seus objetivos',
    cashflow_debt: 'Fluxo de caixa e dívidas',
    retirement: 'Aposentadoria',
    education: 'Educação',
    protection: 'Proteção',
    tax: 'Impostos',
    investments: 'Investimentos',
    work_business: 'Trabalho e empresa',
    estate: 'Sucessão',
    dates_that_matter: 'Datas importantes',
    risk: 'Riscos e resiliência',
    tradeoffs: 'As suas opções',
    next_steps: 'O que fazer a seguir',
    monitoring: 'Implementação e acompanhamento',
    net_worth_statement: 'Demonstrativo de patrimônio líquido',
    cash_flow_and_income: 'Fluxo de caixa e renda',
    balance_sheet: 'Balanço patrimonial',
    judgement_and_options: 'Avaliação e opções',
    actions_and_administration: 'Ações e administração',
    tax_and_sources: 'Impostos e fontes',
    cross_border_tax: 'Tributação internacional',
    tax_evaluation: 'Avaliação tributária',
  },
  caseQ: {
    requestFailed: 'Não foi possível concluir a solicitação. Verifique a sua resposta e tente novamente.',
    refreshFailed: 'Não é possível atualizar o seu plano agora. Tentando novamente…',
    answerFailed: 'Não foi possível registrar essa resposta. Tente novamente.',
    failed: 'Não consegui terminar de revisar o que você enviou. Tente a revisão novamente.',
    reading: 'Lendo o que você enviou…',
    currencyFormat: 'Informe um código de moeda de três letras, como USD, GBP ou EUR.',
    currencyFailed: 'Não foi possível registrar essa moeda.',
    planningCurrencyAria: 'Moeda do plano',
    useCurrency: 'Usar esta moeda',
    analyzing: 'Analisando as suas questões de planejamento…',
    introChoose: 'Estas perguntas ajudam a esclarecer os seus valores. Cada resposta atualiza a parte do plano que depende dela.',
    introFacts: 'Algumas informações das quais o seu plano depende não aparecem no que você enviou, e eu não vou supô-las.',
    statement: (n) => `Declaração ${n}:`,
    person: (n) => `Pessoa ${n}`,
    personInline: (n) => `pessoa ${n}`,
    nameFor: (who) => `Nome de ${who}`,
    saveNames: 'Salvar nomes',
    currencyCodeLabel: 'Código da moeda',
    currencyPlaceholder: 'ex.: USD',
    caseSensitive: 'Os códigos diferenciam maiúsculas: GBP são libras, GBp são pence.',
    replyInChat: 'Responder a esta pergunta no chat',
    needsFactUpdate: 'Esta pergunta precisa que um dado seja atualizado antes de ser resolvida.',
    alsoWorking: 'Também estou analisando o restante das suas questões de planejamento.',
    currencyQuestion: 'Em qual moeda o seu plano deve apresentar os totais?',
  },
  report: {
    complete: 'Revisão do seu caso',
    partial: 'O que consigo estabelecer até agora',
    notComplete: 'Isto não é um plano financeiro completo. Os pontos em aberto são mostrados em vez de supostos.',
  },
  generate: {
    signIn: 'Entre na sua conta para gerar o relatório do seu plano.',
    unreachable: 'O serviço do plano não está acessível — tente novamente.',
    couldNotStart: 'Não foi possível iniciar o relatório — tente novamente em instantes.',
    button: 'Gerar o relatório do meu plano',
    working: 'Analisando o seu plano — calculando cada seção e indicando o que ainda falta a cada uma. Isso costuma levar um ou dois minutos.',
    notProduced: 'Não foi possível gerar o relatório. Nada foi suposto no lugar dele — tente novamente e, se isso se repetir, a falha é nossa, não sua.',
    questionsWaiting: (count, n) => (count === 1
      ? `${n} pergunta do seu caso aguarda no assistente — respondê-la desbloqueia as seções que dependem dela.`
      : `${n} perguntas do seu caso aguardam no assistente — respondê-las desbloqueia as seções que dependem delas.`),
  },
  agenda: {
    status: { awaiting: 'aguardando você', open: 'ainda em aberto', failed: 'cabe a nós corrigir', answered: 'respondido' },
    clarifyTitle: 'Esclareça o seu perfil',
    clarifyBody: 'Isto vem do seu perfil salvo, não deste relatório. Corrigir melhora todos os planos e relatórios que o utilizam.',
    agendaTitle: 'A sua agenda',
    agendaLead: 'O que este relatório analisou sobre a sua situação',
    openCount: (count, n) => (count === 1 ? ` — ${n} item ainda em aberto.` : ` — ${n} itens ainda em aberto.`),
    allAnswered: ' — todos os itens foram respondidos.',
    mismatchTitle: 'Isto não corresponde ao seu perfil salvo',
    mismatchLine: (described, profile) => `: você descreveu ${described}; o seu perfil indica ${profile}.`,
  },
  blocks: {
    questionN: (n) => `Pergunta ${n}`,
    sendNofM: (chosen, total) => `Enviar ${chosen} de ${total}`,
    answerAllFirst: 'Você pode responder a todas antes de enviar.',
    back: 'Voltar',
    next: 'Seguinte',
    typeOwn: 'Ou escreva a sua própria resposta',
    stepOfTotal: (n, total) => `${n} de ${total}`,
    figuresSource: 'De onde vêm estes valores',
    computedByEngine: 'calculado pelo motor de planejamento',
    answerAria: 'Resposta',
    yourAnswer: 'A sua resposta',
    send: 'Enviar',
    pp: (n) => `${n} p.p.`,
  },
  chat: {
    noted: 'Anotado.',
    couldNotRecord: 'Não consegui registrar isso agora.',
    couldNotRecordRetry: 'Não consegui registrar isso agora — tente novamente.',
    replyingTo: 'Respondendo a:',
    cancelReply: 'Cancelar resposta',
  },
}

const fr: MyCIOChatPanelsCopy = {
  review: {
    intro: 'Lancez une revue complète de votre plan sur vos données actuelles : elle est mise en file d’attente, calculée en arrière-plan, puis enregistrée comme nouvelle version du plan.',
    queueButton: 'Mettre une revue complète en file d’attente',
    queuing: 'Mise en file d’attente…',
    checking: 'Vérification de votre revue…',
    queued: 'Votre revue mise à jour est en file d’attente.',
    ahead: (count, n) => (count === 1 ? `${n} revue passe avant la vôtre.` : `${n} revues passent avant la vôtre.`),
    estimatedStart: (eta) => `Début estimé : ${eta}.`,
    waitingWorker: 'Étape en cours : en attente d’un serveur de calcul.',
    provisionalTiming: 'Ce calendrier est une estimation et peut changer.',
    stageProgress: (stage, done, total) => `${stage} — ${done} étape(s) sur ${total} terminée(s).`,
    stageOngoing: (stage) => `${stage}…`,
    estimatedCompletion: (eta) => `Fin estimée : ${eta}.`,
    workerUnresponsive: 'Le serveur de calcul ne s’est pas manifesté récemment. La reprise nécessite un serveur disponible ; le délai de fin est inconnu.',
    draftReady: 'Votre revue de travail est prête.',
    draftReadySub: 'L’analyse terminée est disponible. Le plan enregistré n’a pas été mis à jour.',
    complete: 'Votre revue est terminée.',
    savedVersioned: 'Le plan mis à jour est enregistré et versionné.',
    letterPending: 'Sa lettre est encore en cours de rédaction.',
    letterFailed: 'Le plan a été enregistré, mais sa lettre n’est pas disponible. Vous pouvez toujours consulter les résultats enregistrés.',
    letterUnverified: 'L’état de la lettre de cette version n’a pas pu être confirmé ; les chiffres restent valables.',
    letterIncomplete: 'Il manque une ou plusieurs sections à sa lettre — la page du plan les indique ; les chiffres restent valables.',
    noReference: 'La revue est terminée, mais la référence de son résultat n’est pas disponible.',
    needsInput: 'Votre revue n’a pas encore pu s’exécuter : elle a d’abord besoin d’une réponse de votre part.',
    needsInputSub: 'Répondez dans le chat ou dans votre plan, puis relancez la revue.',
    superseded: 'Vos données ont changé : une revue plus récente couvre les chiffres actuels.',
    failed: 'Cette revue n’a pas pu aboutir.',
    reason: (label) => `Motif : ${label}.`,
    cancelled: 'Cette revue a été annulée.',
    none: 'Aucune revue n’a encore été demandée.',
    accessUnavailable: 'Impossible d’accéder à cette revue. Vérifiez votre connexion et vos droits d’accès.',
    etaAbout: (time) => `vers ${time}`,
    failureReasons: {
      internal_error: 'une erreur interne de notre côté',
      engine_error: 'le moteur de planification a renvoyé une erreur',
      engine_refused: 'le moteur de planification n’a pas pu accepter ces données',
      engine_busy: 'le moteur de planification était occupé',
      engine_deadline: 'le calcul a dépassé le temps imparti',
      persistence_failed: 'le résultat n’a pas pu être enregistré',
      lease_expired: 'le serveur de calcul a cessé de répondre',
      cancelled: 'la revue a été annulée',
      review_status_unavailable: 'l’état de la revue n’a pas pu être lu',
      review_access_unavailable: 'cette revue n’est pas accessible',
      other: 'une erreur inattendue',
    },
    stages: {
      load_snapshot: 'Chargement de vos données figées',
      compute_review: 'Exécution de la simulation de Monte-Carlo',
      publish_plan: 'Enregistrement de la nouvelle version de votre plan',
      needs_input: 'Des informations supplémentaires sont nécessaires pour calculer ce plan',
      computing: 'Calcul en cours',
      working: 'Traitement en cours',
    },
    retrievalError: 'Votre revue est terminée, mais ses résultats n’ont pas pu être chargés. Nouvelle tentative de livraison, sans relancer le calcul.',
    retryLoading: 'Réessayer le chargement',
    notUpdated: 'Le plan enregistré n’a pas été mis à jour.',
    notCompleted: 'Non terminé lors de cette exécution',
    questionsYouCanAnswer: 'Questions auxquelles vous pouvez répondre',
    runNow: 'Lancer maintenant',
    cancel: 'Annuler',
    reviewAgain: 'Relancer la revue',
    dismiss: 'Fermer',
  },
  sections: {
    engagement_scope: 'Votre question et le périmètre de ce plan',
    assumptions: 'Les hypothèses derrière les chiffres',
    executive_summary: 'Synthèse sur une page',
    current_condition: 'Votre situation aujourd’hui',
    tax_position: 'Là où la fiscalité change la réponse',
    swot: 'Risques significatifs et résilience',
    recommendations_and_options: 'Les options qui s’offrent à vous',
    implementation_actions: 'Prochaines étapes et qui s’en charge',
    monitoring_and_review: 'Quand ce plan sera revu',
    professionals: 'Autres professionnels nécessaires à ce plan',
    sections_not_provided: 'Sections non fournies',
    open_items: 'Points ouverts',
    executive_letter: 'Votre plan aujourd’hui',
    overview: 'Vue d’ensemble',
    what_you_asked: 'Votre question',
    situation: 'Votre situation',
    goals: 'Objectifs',
    personal_and_goals: 'Vous et vos objectifs',
    cashflow_debt: 'Flux de trésorerie et dettes',
    retirement: 'Retraite',
    education: 'Études',
    protection: 'Prévoyance',
    tax: 'Fiscalité',
    investments: 'Placements',
    work_business: 'Activité et entreprise',
    estate: 'Succession',
    dates_that_matter: 'Dates clés',
    risk: 'Risques et résilience',
    tradeoffs: 'Vos options',
    next_steps: 'Prochaines étapes',
    monitoring: 'Mise en œuvre et suivi',
    net_worth_statement: 'État du patrimoine net',
    cash_flow_and_income: 'Flux de trésorerie et revenus',
    balance_sheet: 'Bilan patrimonial',
    judgement_and_options: 'Appréciation et options',
    actions_and_administration: 'Actions et gestion administrative',
    tax_and_sources: 'Fiscalité et sources',
    cross_border_tax: 'Fiscalité transfrontalière',
    tax_evaluation: 'Évaluation fiscale',
  },
  caseQ: {
    requestFailed: 'La demande n’a pas pu aboutir. Vérifiez votre réponse et réessayez.',
    refreshFailed: 'Impossible d’actualiser votre plan pour le moment. Nouvelle tentative…',
    answerFailed: 'Cette réponse n’a pas pu être enregistrée. Veuillez réessayer.',
    failed: 'Je n’ai pas pu terminer l’examen de ce que vous avez envoyé. Veuillez relancer la revue.',
    reading: 'Lecture de ce que vous avez envoyé…',
    currencyFormat: 'Saisissez un code de devise à trois lettres, par exemple USD, GBP ou EUR.',
    currencyFailed: 'Cette devise n’a pas pu être enregistrée.',
    planningCurrencyAria: 'Devise du plan',
    useCurrency: 'Utiliser cette devise',
    analyzing: 'Analyse de vos questions patrimoniales…',
    introChoose: 'Ces questions permettent de préciser vos chiffres. Chaque réponse met à jour la partie du plan qui en dépend.',
    introFacts: 'Certains éléments dont dépend votre plan ne figurent nulle part dans ce que vous avez envoyé, et je ne les supposerai pas.',
    statement: (n) => `Déclaration ${n} :`,
    person: (n) => `Personne ${n}`,
    personInline: (n) => `personne ${n}`,
    nameFor: (who) => `Nom pour ${who}`,
    saveNames: 'Enregistrer les noms',
    currencyCodeLabel: 'Code de devise',
    currencyPlaceholder: 'ex. USD',
    caseSensitive: 'Les codes respectent la casse : GBP désigne la livre, GBp le penny.',
    replyInChat: 'Répondre à cette question dans le chat',
    needsFactUpdate: 'Cette question nécessite la mise à jour d’une donnée avant de pouvoir être tranchée.',
    alsoWorking: 'J’analyse aussi le reste de vos questions patrimoniales.',
    currencyQuestion: 'Dans quelle devise votre plan doit-il exprimer ses totaux ?',
  },
  report: {
    complete: 'La revue de votre dossier',
    partial: 'Ce que je peux établir à ce stade',
    notComplete: 'Ceci n’est pas un plan financier complet. Les points ouverts sont signalés plutôt que supposés.',
  },
  generate: {
    signIn: 'Connectez-vous pour générer le rapport de votre plan.',
    unreachable: 'Le service du plan est injoignable — réessayez.',
    couldNotStart: 'Impossible de lancer le rapport — réessayez dans un instant.',
    button: 'Générer le rapport de mon plan',
    working: 'Analyse de votre plan : chaque section est calculée, et ce qui lui manque encore est indiqué. Cela prend généralement une à deux minutes.',
    notProduced: 'Le rapport n’a pas pu être produit. Rien n’a été supposé à sa place — veuillez réessayer ; si cela se reproduit, l’erreur est de notre côté, pas du vôtre.',
    questionsWaiting: (count, n) => (count === 1
      ? `${n} question de votre dossier vous attend dans l’assistant — y répondre débloque les sections qui en dépendent.`
      : `${n} questions de votre dossier vous attendent dans l’assistant — y répondre débloque les sections qui en dépendent.`),
  },
  agenda: {
    status: { awaiting: 'en attente de vous', open: 'encore ouvert', failed: 'à corriger de notre côté', answered: 'répondu' },
    clarifyTitle: 'Précisez votre profil',
    clarifyBody: 'Ces points viennent de votre profil enregistré, pas de ce rapport. Les corriger améliore chaque plan et rapport qui s’en sert.',
    agendaTitle: 'Votre ordre du jour',
    agendaLead: 'Ce que ce rapport a examiné dans votre situation',
    openCount: (count, n) => (count === 1 ? ` — ${n} point reste ouvert.` : ` — ${n} points restent ouverts.`),
    allAnswered: ' — tous les points ont une réponse.',
    mismatchTitle: 'Ceci ne correspond pas à votre profil enregistré',
    mismatchLine: (described, profile) => ` : vous avez indiqué ${described} ; votre profil indique ${profile}.`,
  },
  blocks: {
    questionN: (n) => `Question ${n}`,
    sendNofM: (chosen, total) => `Envoyer ${chosen} sur ${total}`,
    answerAllFirst: 'Vous pouvez toutes y répondre avant d’envoyer.',
    back: 'Retour',
    next: 'Suivant',
    typeOwn: 'Ou écrivez votre propre réponse',
    stepOfTotal: (n, total) => `${n} sur ${total}`,
    figuresSource: 'D’où viennent ces chiffres',
    computedByEngine: 'calculé par le moteur de planification',
    answerAria: 'Réponse',
    yourAnswer: 'Votre réponse',
    send: 'Envoyer',
    pp: (n) => `${n} pts`,
  },
  chat: {
    noted: 'C’est noté.',
    couldNotRecord: 'Je n’ai pas pu l’enregistrer pour le moment.',
    couldNotRecordRetry: 'Je n’ai pas pu l’enregistrer pour le moment — veuillez réessayer.',
    replyingTo: 'En réponse à :',
    cancelReply: 'Annuler la réponse',
  },
}

const de: MyCIOChatPanelsCopy = {
  review: {
    intro: 'Lassen Sie Ihren Plan mit Ihren aktuellen Angaben vollständig prüfen – die Prüfung wird eingereiht, im Hintergrund berechnet und als neue Planversion gespeichert.',
    queueButton: 'Vollständige Prüfung einreihen',
    queuing: 'Wird eingereiht…',
    checking: 'Ihre Prüfung wird abgefragt…',
    queued: 'Ihre aktualisierte Prüfung ist eingereiht.',
    ahead: (count, n) => (count === 1 ? `${n} Prüfung ist vor Ihrer an der Reihe.` : `${n} Prüfungen sind vor Ihrer an der Reihe.`),
    estimatedStart: (eta) => `Voraussichtlicher Beginn: ${eta}.`,
    waitingWorker: 'Aktueller Schritt: Warten auf einen Rechenserver.',
    provisionalTiming: 'Dieser Zeitpunkt ist eine Schätzung und kann sich ändern.',
    stageProgress: (stage, done, total) => `${stage} – ${done} von ${total} Schritten abgeschlossen.`,
    stageOngoing: (stage) => `${stage}…`,
    estimatedCompletion: (eta) => `Voraussichtlicher Abschluss: ${eta}.`,
    workerUnresponsive: 'Der Rechenserver hat sich seit einiger Zeit nicht gemeldet. Fortgesetzt wird erst, wenn ein Server verfügbar ist; der Abschlusszeitpunkt ist unbekannt.',
    draftReady: 'Ihre Arbeitsfassung der Prüfung liegt vor.',
    draftReadySub: 'Die abgeschlossene Analyse ist verfügbar. Der gespeicherte Plan wurde nicht aktualisiert.',
    complete: 'Ihre Prüfung ist abgeschlossen.',
    savedVersioned: 'Der aktualisierte Plan ist gespeichert und versioniert.',
    letterPending: 'Das zugehörige Anschreiben wird noch verfasst.',
    letterFailed: 'Der Plan wurde gespeichert, das Anschreiben ist jedoch nicht verfügbar. Die gespeicherten Ergebnisse können Sie weiterhin einsehen.',
    letterUnverified: 'Der Status des Anschreibens für diese Version ließ sich nicht bestätigen; die Zahlen bleiben gültig.',
    letterIncomplete: 'Im Anschreiben fehlen ein oder mehrere Abschnitte – die Planseite nennt sie; die Zahlen bleiben gültig.',
    noReference: 'Die Prüfung ist beendet, aber der Verweis auf ihr Ergebnis ist nicht verfügbar.',
    needsInput: 'Ihre Prüfung konnte noch nicht laufen – zuerst wird eine Antwort von Ihnen benötigt.',
    needsInputSub: 'Antworten Sie im Chat oder in Ihrem Plan und fordern Sie die Prüfung dann erneut an.',
    superseded: 'Ihre Angaben haben sich geändert – eine neuere Prüfung deckt die aktuellen Zahlen ab.',
    failed: 'Diese Prüfung konnte nicht abgeschlossen werden.',
    reason: (label) => `Grund: ${label}.`,
    cancelled: 'Diese Prüfung wurde abgebrochen.',
    none: 'Es wurde noch keine Prüfung angefordert.',
    accessUnavailable: 'Auf diese Prüfung kann nicht zugegriffen werden. Bitte prüfen Sie Ihre Anmeldung und Ihre Berechtigungen.',
    etaAbout: (time) => `gegen ${time}`,
    failureReasons: {
      internal_error: 'ein interner Fehler auf unserer Seite',
      engine_error: 'die Planungs-Engine hat einen Fehler gemeldet',
      engine_refused: 'die Planungs-Engine konnte diese Angaben nicht übernehmen',
      engine_busy: 'die Planungs-Engine war ausgelastet',
      engine_deadline: 'die Berechnung hat das Zeitlimit überschritten',
      persistence_failed: 'das Ergebnis konnte nicht gespeichert werden',
      lease_expired: 'der Rechenserver hat nicht mehr geantwortet',
      cancelled: 'die Prüfung wurde abgebrochen',
      review_status_unavailable: 'der Status der Prüfung konnte nicht gelesen werden',
      review_access_unavailable: 'auf diese Prüfung konnte nicht zugegriffen werden',
      other: 'ein unerwarteter Fehler',
    },
    stages: {
      load_snapshot: 'Ihre eingefrorenen Angaben werden geladen',
      compute_review: 'Die Monte-Carlo-Simulation läuft',
      publish_plan: 'Ihre neue Planversion wird gespeichert',
      needs_input: 'Für die Berechnung dieses Plans werden weitere Angaben benötigt',
      computing: 'Berechnung läuft',
      working: 'In Bearbeitung',
    },
    retrievalError: 'Ihre Prüfung ist abgeschlossen, die Ergebnisse konnten aber nicht geladen werden. Die Zustellung wird erneut versucht, ohne neu zu rechnen.',
    retryLoading: 'Erneut laden',
    notUpdated: 'Der gespeicherte Plan wurde nicht aktualisiert.',
    notCompleted: 'In diesem Durchlauf nicht abgeschlossen',
    questionsYouCanAnswer: 'Fragen, die Sie beantworten können',
    runNow: 'Jetzt ausführen',
    cancel: 'Abbrechen',
    reviewAgain: 'Erneut prüfen',
    dismiss: 'Ausblenden',
  },
  sections: {
    engagement_scope: 'Ihre Frage und der Umfang dieses Plans',
    assumptions: 'Die Annahmen hinter den Zahlen',
    executive_summary: 'Zusammenfassung auf einer Seite',
    current_condition: 'Ihre heutige Ausgangslage',
    tax_position: 'Wo Steuern die Antwort verändern',
    swot: 'Wesentliche Risiken und Widerstandsfähigkeit',
    recommendations_and_options: 'Die Optionen, die Ihnen offenstehen',
    implementation_actions: 'Die nächsten Schritte und wer sie übernimmt',
    monitoring_and_review: 'Wann dieser Plan erneut überprüft wird',
    professionals: 'Weitere Fachleute, die dieser Plan braucht',
    sections_not_provided: 'Nicht gelieferte Abschnitte',
    open_items: 'Offene Punkte',
    executive_letter: 'Ihr Plan heute',
    overview: 'Überblick',
    what_you_asked: 'Ihre Frage',
    situation: 'Ihre Situation',
    goals: 'Ziele',
    personal_and_goals: 'Sie und Ihre Ziele',
    cashflow_debt: 'Cashflow & Schulden',
    retirement: 'Ruhestand',
    education: 'Ausbildung',
    protection: 'Absicherung',
    tax: 'Steuern',
    investments: 'Geldanlage',
    work_business: 'Beruf & Unternehmen',
    estate: 'Nachlass',
    dates_that_matter: 'Wichtige Termine',
    risk: 'Risiken und Widerstandsfähigkeit',
    tradeoffs: 'Ihre Optionen',
    next_steps: 'Die nächsten Schritte',
    monitoring: 'Umsetzung und Überwachung',
    net_worth_statement: 'Vermögensaufstellung',
    cash_flow_and_income: 'Cashflow und Einkommen',
    balance_sheet: 'Vermögensbilanz',
    judgement_and_options: 'Einschätzung und Optionen',
    actions_and_administration: 'Maßnahmen und Verwaltung',
    tax_and_sources: 'Steuern und Quellen',
    cross_border_tax: 'Grenzüberschreitende Besteuerung',
    tax_evaluation: 'Steuerliche Bewertung',
  },
  caseQ: {
    requestFailed: 'Die Anfrage konnte nicht abgeschlossen werden. Bitte prüfen Sie Ihre Antwort und versuchen Sie es erneut.',
    refreshFailed: 'Ihr Plan kann gerade nicht aktualisiert werden. Neuer Versuch…',
    answerFailed: 'Diese Antwort konnte nicht erfasst werden. Bitte versuchen Sie es erneut.',
    failed: 'Ich konnte die Prüfung Ihrer Angaben nicht abschließen. Bitte starten Sie die Prüfung erneut.',
    reading: 'Ihre Angaben werden gelesen…',
    currencyFormat: 'Geben Sie einen dreistelligen Währungscode ein, etwa USD, GBP oder EUR.',
    currencyFailed: 'Diese Währung konnte nicht erfasst werden.',
    planningCurrencyAria: 'Planungswährung',
    useCurrency: 'Diese Währung verwenden',
    analyzing: 'Ihre Planungsfragen werden bearbeitet…',
    introChoose: 'Diese Fragen helfen, Ihre Zahlen zu präzisieren. Jede Antwort aktualisiert den Teil Ihres Plans, der davon abhängt.',
    introFacts: 'Einige Angaben, von denen Ihr Plan abhängt, stehen nirgends in Ihrer Nachricht – und ich werde sie nicht erraten.',
    statement: (n) => `Angabe ${n}:`,
    person: (n) => `Person ${n}`,
    personInline: (n) => `Person ${n}`,
    nameFor: (who) => `Name für ${who}`,
    saveNames: 'Namen speichern',
    currencyCodeLabel: 'Währungscode',
    currencyPlaceholder: 'z. B. USD',
    caseSensitive: 'Groß- und Kleinschreibung zählt: GBP steht für Pfund, GBp für Pence.',
    replyInChat: 'Diese Frage im Chat beantworten',
    needsFactUpdate: 'Für diese Frage muss zuerst eine Angabe aktualisiert werden.',
    alsoWorking: 'Ich bearbeite außerdem Ihre übrigen Planungsfragen.',
    currencyQuestion: 'In welcher Währung soll Ihr Plan seine Summen ausweisen?',
  },
  report: {
    complete: 'Die Prüfung Ihres Falls',
    partial: 'Was ich bisher feststellen kann',
    notComplete: 'Dies ist kein vollständiger Finanzplan. Offene Punkte werden benannt statt geschätzt.',
  },
  generate: {
    signIn: 'Melden Sie sich an, um Ihren Planbericht zu erstellen.',
    unreachable: 'Der Plandienst ist nicht erreichbar – bitte erneut versuchen.',
    couldNotStart: 'Der Bericht konnte nicht gestartet werden – bitte gleich erneut versuchen.',
    button: 'Meinen Planbericht erstellen',
    working: 'Ihr Plan wird bearbeitet – jeder Abschnitt wird berechnet, und es wird benannt, worauf ein Abschnitt noch wartet. Das dauert meist ein bis zwei Minuten.',
    notProduced: 'Der Bericht konnte nicht erstellt werden. An seiner Stelle wurde nichts geschätzt – bitte versuchen Sie es erneut; tritt das wieder auf, liegt der Fehler bei uns, nicht bei Ihnen.',
    questionsWaiting: (count, n) => (count === 1
      ? `${n} Frage zu Ihrem Fall wartet im Assistenten – ihre Beantwortung gibt die davon abhängigen Abschnitte frei.`
      : `${n} Fragen zu Ihrem Fall warten im Assistenten – ihre Beantwortung gibt die davon abhängigen Abschnitte frei.`),
  },
  agenda: {
    status: { awaiting: 'wartet auf Sie', open: 'noch offen', failed: 'von uns zu beheben', answered: 'beantwortet' },
    clarifyTitle: 'Ihr Profil klären',
    clarifyBody: 'Diese Punkte stammen aus Ihrem gespeicherten Profil, nicht aus diesem Bericht. Ihre Korrektur verbessert jeden Plan und Bericht, der darauf zugreift.',
    agendaTitle: 'Ihre Agenda',
    agendaLead: 'Was dieser Bericht zu Ihrer Situation bearbeitet hat',
    openCount: (count, n) => (count === 1 ? ` – ${n} Punkt ist noch offen.` : ` – ${n} Punkte sind noch offen.`),
    allAnswered: ' – alle Punkte sind beantwortet.',
    mismatchTitle: 'Das stimmt nicht mit Ihrem gespeicherten Profil überein',
    mismatchLine: (described, profile) => `: Sie haben ${described} beschrieben; Ihr Profil nennt ${profile}.`,
  },
  blocks: {
    questionN: (n) => `Frage ${n}`,
    sendNofM: (chosen, total) => `${chosen} von ${total} senden`,
    answerAllFirst: 'Sie können alle beantworten, bevor Sie senden.',
    back: 'Zurück',
    next: 'Weiter',
    typeOwn: 'Oder eigene Antwort eingeben',
    stepOfTotal: (n, total) => `${n} von ${total}`,
    figuresSource: 'Woher diese Zahlen stammen',
    computedByEngine: 'von der Planungs-Engine berechnet',
    answerAria: 'Antwort',
    yourAnswer: 'Ihre Antwort',
    send: 'Senden',
    pp: (n) => `${n} Pp.`,
  },
  chat: {
    noted: 'Notiert.',
    couldNotRecord: 'Das konnte ich gerade nicht erfassen.',
    couldNotRecordRetry: 'Das konnte ich gerade nicht erfassen – bitte versuchen Sie es erneut.',
    replyingTo: 'Antwort auf:',
    cancelReply: 'Antwort abbrechen',
  },
}

const it: MyCIOChatPanelsCopy = {
  review: {
    intro: 'Avvia una revisione completa del tuo piano sui dati attuali: entra in coda, viene calcolata in background e salva una nuova versione del piano.',
    queueButton: 'Metti in coda una revisione completa',
    queuing: 'Messa in coda…',
    checking: 'Verifica della tua revisione…',
    queued: 'La tua revisione aggiornata è in coda.',
    ahead: (count, n) => (count === 1 ? `C’è ${n} revisione prima della tua.` : `Ci sono ${n} revisioni prima della tua.`),
    estimatedStart: (eta) => `Inizio stimato: ${eta}.`,
    waitingWorker: 'Fase attuale: in attesa di un server di calcolo.',
    provisionalTiming: 'Questa tempistica è una stima e potrebbe cambiare.',
    stageProgress: (stage, done, total) => `${stage} — ${done} fasi su ${total} completate.`,
    stageOngoing: (stage) => `${stage}…`,
    estimatedCompletion: (eta) => `Completamento stimato: ${eta}.`,
    workerUnresponsive: 'Il server di calcolo non risponde da un po’. Per riprendere serve un server disponibile; i tempi di completamento non sono noti.',
    draftReady: 'La tua revisione di lavoro è pronta.',
    draftReadySub: 'L’analisi completata è disponibile. Il piano salvato non è stato aggiornato.',
    complete: 'La tua revisione è completata.',
    savedVersioned: 'Il piano aggiornato è salvato e versionato.',
    letterPending: 'La lettera è ancora in fase di redazione.',
    letterFailed: 'Il piano è stato salvato, ma la lettera non è disponibile. Puoi comunque consultare i risultati salvati.',
    letterUnverified: 'Non è stato possibile confermare lo stato della lettera per questa versione; le cifre restano valide.',
    letterIncomplete: 'Alla lettera mancano una o più sezioni — la pagina del piano le indica; le cifre restano valide.',
    noReference: 'La revisione è terminata, ma il riferimento al risultato non è disponibile.',
    needsInput: 'La tua revisione non è ancora potuta partire: prima serve una tua risposta.',
    needsInputSub: 'Rispondi nella chat o nel tuo piano, poi richiedi di nuovo la revisione.',
    superseded: 'I tuoi dati sono cambiati: una revisione più recente copre le cifre attuali.',
    failed: 'Questa revisione non è stata completata.',
    reason: (label) => `Motivo: ${label}.`,
    cancelled: 'Questa revisione è stata annullata.',
    none: 'Non è stata ancora richiesta alcuna revisione.',
    accessUnavailable: 'Impossibile accedere a questa revisione. Verifica l’accesso e le autorizzazioni.',
    etaAbout: (time) => `verso le ${time}`,
    failureReasons: {
      internal_error: 'un errore interno da parte nostra',
      engine_error: 'il motore di pianificazione ha restituito un errore',
      engine_refused: 'il motore di pianificazione non ha potuto accettare questi dati',
      engine_busy: 'il motore di pianificazione era occupato',
      engine_deadline: 'il calcolo ha superato il tempo disponibile',
      persistence_failed: 'non è stato possibile salvare il risultato',
      lease_expired: 'il server di calcolo ha smesso di rispondere',
      cancelled: 'la revisione è stata annullata',
      review_status_unavailable: 'non è stato possibile leggere lo stato della revisione',
      review_access_unavailable: 'non è stato possibile accedere a questa revisione',
      other: 'un errore imprevisto',
    },
    stages: {
      load_snapshot: 'Caricamento dei tuoi dati congelati',
      compute_review: 'Esecuzione della simulazione Monte Carlo',
      publish_plan: 'Salvataggio della nuova versione del piano',
      needs_input: 'Servono altre informazioni prima di poter calcolare questo piano',
      computing: 'Calcolo in corso',
      working: 'Elaborazione in corso',
    },
    retrievalError: 'La revisione è terminata, ma non è stato possibile caricarne i risultati. Nuovo tentativo di consegna senza ripetere il calcolo.',
    retryLoading: 'Riprova a caricare',
    notUpdated: 'Il piano salvato non è stato aggiornato.',
    notCompleted: 'Non completato in questa esecuzione',
    questionsYouCanAnswer: 'Domande a cui puoi rispondere',
    runNow: 'Avvia ora',
    cancel: 'Annulla',
    reviewAgain: 'Rivedi di nuovo',
    dismiss: 'Chiudi',
  },
  sections: {
    engagement_scope: 'La tua domanda e il perimetro di questo piano',
    assumptions: 'Le ipotesi alla base delle cifre',
    executive_summary: 'Sintesi in una pagina',
    current_condition: 'La tua situazione oggi',
    tax_position: 'Dove la fiscalità cambia la risposta',
    swot: 'Rischi rilevanti e resilienza',
    recommendations_and_options: 'Le opzioni a tua disposizione',
    implementation_actions: 'I prossimi passi e chi li compie',
    monitoring_and_review: 'Quando questo piano verrà rivisto',
    professionals: 'Altri professionisti necessari a questo piano',
    sections_not_provided: 'Sezioni non fornite',
    open_items: 'Punti aperti',
    executive_letter: 'Il tuo piano oggi',
    overview: 'Panoramica',
    what_you_asked: 'La tua domanda',
    situation: 'La tua situazione',
    goals: 'Obiettivi',
    personal_and_goals: 'Tu e i tuoi obiettivi',
    cashflow_debt: 'Flussi di cassa e debiti',
    retirement: 'Pensione',
    education: 'Istruzione',
    protection: 'Protezione',
    tax: 'Fiscalità',
    investments: 'Investimenti',
    work_business: 'Lavoro e impresa',
    estate: 'Successione',
    dates_that_matter: 'Date importanti',
    risk: 'Rischi e resilienza',
    tradeoffs: 'Le tue opzioni',
    next_steps: 'Cosa fare adesso',
    monitoring: 'Attuazione e monitoraggio',
    net_worth_statement: 'Prospetto del patrimonio netto',
    cash_flow_and_income: 'Flussi di cassa e redditi',
    balance_sheet: 'Stato patrimoniale',
    judgement_and_options: 'Valutazione e opzioni',
    actions_and_administration: 'Azioni e amministrazione',
    tax_and_sources: 'Fiscalità e fonti',
    cross_border_tax: 'Fiscalità transfrontaliera',
    tax_evaluation: 'Valutazione fiscale',
  },
  caseQ: {
    requestFailed: 'Non è stato possibile completare la richiesta. Controlla la risposta e riprova.',
    refreshFailed: 'Al momento non è possibile aggiornare il tuo piano. Nuovo tentativo…',
    answerFailed: 'Non è stato possibile registrare questa risposta. Riprova.',
    failed: 'Non sono riuscito a completare l’esame di ciò che hai inviato. Riprova la revisione.',
    reading: 'Lettura di ciò che hai inviato…',
    currencyFormat: 'Inserisci un codice valuta di tre lettere, ad esempio USD, GBP o EUR.',
    currencyFailed: 'Non è stato possibile registrare questa valuta.',
    planningCurrencyAria: 'Valuta del piano',
    useCurrency: 'Usa questa valuta',
    analyzing: 'Sto esaminando le tue domande di pianificazione…',
    introChoose: 'Queste domande aiutano a precisare le tue cifre. Ogni risposta aggiorna la parte del piano che ne dipende.',
    introFacts: 'Alcuni elementi da cui dipende il tuo piano non compaiono in ciò che hai inviato, e non li darò per scontati.',
    statement: (n) => `Dichiarazione ${n}:`,
    person: (n) => `Persona ${n}`,
    personInline: (n) => `persona ${n}`,
    nameFor: (who) => `Nome per ${who}`,
    saveNames: 'Salva i nomi',
    currencyCodeLabel: 'Codice valuta',
    currencyPlaceholder: 'es. USD',
    caseSensitive: 'I codici distinguono maiuscole e minuscole: GBP sono sterline, GBp sono pence.',
    replyInChat: 'Rispondi a questa domanda nella chat',
    needsFactUpdate: 'Per risolvere questa domanda occorre prima aggiornare un dato.',
    alsoWorking: 'Sto esaminando anche le altre tue domande di pianificazione.',
    currencyQuestion: 'In quale valuta il tuo piano deve esprimere i totali?',
  },
  report: {
    complete: 'La revisione del tuo caso',
    partial: 'Ciò che posso stabilire finora',
    notComplete: 'Questo non è un piano finanziario completo. I punti aperti sono indicati, non ipotizzati.',
  },
  generate: {
    signIn: 'Accedi per generare il report del tuo piano.',
    unreachable: 'Il servizio del piano non è raggiungibile — riprova.',
    couldNotStart: 'Impossibile avviare il report — riprova tra poco.',
    button: 'Genera il report del mio piano',
    working: 'Sto esaminando il tuo piano: calcolo ogni sezione e indico cosa manca ancora a ciascuna. Di solito ci vogliono uno o due minuti.',
    notProduced: 'Non è stato possibile produrre il report. Non è stato ipotizzato nulla al suo posto — riprova e, se il problema si ripete, l’errore è nostro, non tuo.',
    questionsWaiting: (count, n) => (count === 1
      ? `${n} domanda del tuo caso ti aspetta nell’assistente: rispondere sblocca le sezioni che ne dipendono.`
      : `${n} domande del tuo caso ti aspettano nell’assistente: rispondere sblocca le sezioni che ne dipendono.`),
  },
  agenda: {
    status: { awaiting: 'in attesa di te', open: 'ancora aperto', failed: 'spetta a noi risolverlo', answered: 'risposto' },
    clarifyTitle: 'Chiarisci il tuo profilo',
    clarifyBody: 'Questi punti vengono dal tuo profilo salvato, non da questo report. Correggerli migliora ogni piano e report che lo utilizza.',
    agendaTitle: 'La tua agenda',
    agendaLead: 'Ciò che questo report ha esaminato sulla tua situazione',
    openCount: (count, n) => (count === 1 ? ` — ${n} punto è ancora aperto.` : ` — ${n} punti sono ancora aperti.`),
    allAnswered: ' — tutti i punti hanno una risposta.',
    mismatchTitle: 'Questo non corrisponde al tuo profilo salvato',
    mismatchLine: (described, profile) => `: hai descritto ${described}; il tuo profilo indica ${profile}.`,
  },
  blocks: {
    questionN: (n) => `Domanda ${n}`,
    sendNofM: (chosen, total) => `Invia ${chosen} di ${total}`,
    answerAllFirst: 'Puoi rispondere a tutte prima di inviare.',
    back: 'Indietro',
    next: 'Avanti',
    typeOwn: 'Oppure scrivi la tua risposta',
    stepOfTotal: (n, total) => `${n} di ${total}`,
    figuresSource: 'Da dove vengono queste cifre',
    computedByEngine: 'calcolato dal motore di pianificazione',
    answerAria: 'Risposta',
    yourAnswer: 'La tua risposta',
    send: 'Invia',
    pp: (n) => `${n} p.p.`,
  },
  chat: {
    noted: 'Annotato.',
    couldNotRecord: 'Non sono riuscito a registrarlo in questo momento.',
    couldNotRecordRetry: 'Non sono riuscito a registrarlo in questo momento — riprova.',
    replyingTo: 'In risposta a:',
    cancelReply: 'Annulla risposta',
  },
}

const ru: MyCIOChatPanelsCopy = {
  review: {
    intro: 'Запустите полный пересмотр плана на текущих данных — он встанет в очередь, будет рассчитан в фоновом режиме и сохранится как новая версия плана.',
    queueButton: 'Поставить полный пересмотр в очередь',
    queuing: 'Постановка в очередь…',
    checking: 'Проверяем статус пересмотра…',
    queued: 'Ваш обновлённый пересмотр стоит в очереди.',
    ahead: (count, n) => `Перед вами в очереди ${n} ${ruPlural(count, 'пересмотр', 'пересмотра', 'пересмотров')}.`,
    estimatedStart: (eta) => `Ориентировочное начало: ${eta}.`,
    waitingWorker: 'Текущий этап: ожидание вычислительного сервера.',
    provisionalTiming: 'Эти сроки приблизительные и могут измениться.',
    stageProgress: (stage, done, total) => `${stage} — завершено этапов: ${done} из ${total}.`,
    stageOngoing: (stage) => `${stage}…`,
    estimatedCompletion: (eta) => `Ориентировочное завершение: ${eta}.`,
    workerUnresponsive: 'Вычислительный сервер давно не выходил на связь. Для продолжения нужен свободный сервер; срок завершения неизвестен.',
    draftReady: 'Рабочая версия пересмотра готова.',
    draftReadySub: 'Завершённый анализ доступен. Сохранённый план не обновлялся.',
    complete: 'Пересмотр завершён.',
    savedVersioned: 'Обновлённый план сохранён как новая версия.',
    letterPending: 'Письмо к нему ещё готовится.',
    letterFailed: 'План сохранён, но письмо к нему недоступно. Сохранённые результаты по-прежнему можно посмотреть.',
    letterUnverified: 'Статус письма для этой версии подтвердить не удалось; цифры остаются в силе.',
    letterIncomplete: 'В письме не хватает одного или нескольких разделов — они названы на странице плана; цифры остаются в силе.',
    noReference: 'Пересмотр завершён, но ссылка на его результат недоступна.',
    needsInput: 'Пересмотр пока не может запуститься — сначала нужен ваш ответ.',
    needsInputSub: 'Ответьте в чате или в плане, а затем снова запросите пересмотр.',
    superseded: 'Ваши данные изменились — актуальные цифры охватывает более новый пересмотр.',
    failed: 'Этот пересмотр не удалось завершить.',
    reason: (label) => `Причина: ${label}.`,
    cancelled: 'Этот пересмотр отменён.',
    none: 'Пересмотр ещё не запрашивался.',
    accessUnavailable: 'Нет доступа к этому пересмотру. Проверьте вход в аккаунт и права доступа.',
    etaAbout: (time) => `около ${time}`,
    failureReasons: {
      internal_error: 'внутренняя ошибка на нашей стороне',
      engine_error: 'модуль планирования вернул ошибку',
      engine_refused: 'модуль планирования не смог принять эти данные',
      engine_busy: 'модуль планирования был занят',
      engine_deadline: 'расчёт не уложился в отведённое время',
      persistence_failed: 'результат не удалось сохранить',
      lease_expired: 'вычислительный сервер перестал отвечать',
      cancelled: 'пересмотр был отменён',
      review_status_unavailable: 'не удалось прочитать статус пересмотра',
      review_access_unavailable: 'нет доступа к этому пересмотру',
      other: 'непредвиденная ошибка',
    },
    stages: {
      load_snapshot: 'Загружаем зафиксированные данные',
      compute_review: 'Выполняется моделирование методом Монте-Карло',
      publish_plan: 'Сохраняем новую версию плана',
      needs_input: 'Для расчёта плана нужна дополнительная информация',
      computing: 'Идёт расчёт',
      working: 'Выполняется',
    },
    retrievalError: 'Пересмотр завершён, но загрузить результаты не удалось. Повторяем доставку без повторного расчёта.',
    retryLoading: 'Загрузить снова',
    notUpdated: 'Сохранённый план не обновлялся.',
    notCompleted: 'Не завершено в этом запуске',
    questionsYouCanAnswer: 'Вопросы, на которые вы можете ответить',
    runNow: 'Запустить сейчас',
    cancel: 'Отменить',
    reviewAgain: 'Пересмотреть снова',
    dismiss: 'Скрыть',
  },
  sections: {
    engagement_scope: 'Ваш вопрос и что охватывает этот план',
    assumptions: 'Допущения, лежащие в основе цифр',
    executive_summary: 'Краткое резюме на одной странице',
    current_condition: 'Ваше положение сегодня',
    tax_position: 'Где налоги меняют ответ',
    swot: 'Существенные риски и устойчивость',
    recommendations_and_options: 'Доступные вам варианты',
    implementation_actions: 'Дальнейшие шаги и кто их выполняет',
    monitoring_and_review: 'Когда этот план будет пересмотрен',
    professionals: 'Другие специалисты, нужные этому плану',
    sections_not_provided: 'Непредоставленные разделы',
    open_items: 'Открытые вопросы',
    executive_letter: 'Ваш план сейчас',
    overview: 'Обзор',
    what_you_asked: 'Ваш вопрос',
    situation: 'Ваша ситуация',
    goals: 'Цели',
    personal_and_goals: 'Вы и ваши цели',
    cashflow_debt: 'Денежный поток и долги',
    retirement: 'Пенсия',
    education: 'Образование',
    protection: 'Страховая защита',
    tax: 'Налоги',
    investments: 'Инвестиции',
    work_business: 'Работа и бизнес',
    estate: 'Наследство',
    dates_that_matter: 'Важные даты',
    risk: 'Риски и устойчивость',
    tradeoffs: 'Ваши варианты',
    next_steps: 'Что делать дальше',
    monitoring: 'Реализация и мониторинг',
    net_worth_statement: 'Отчёт о чистых активах',
    cash_flow_and_income: 'Денежный поток и доходы',
    balance_sheet: 'Личный баланс',
    judgement_and_options: 'Оценка и варианты',
    actions_and_administration: 'Действия и администрирование',
    tax_and_sources: 'Налоги и источники',
    cross_border_tax: 'Международное налогообложение',
    tax_evaluation: 'Налоговая оценка',
  },
  caseQ: {
    requestFailed: 'Не удалось выполнить запрос. Проверьте ответ и попробуйте ещё раз.',
    refreshFailed: 'Сейчас не удаётся обновить ваш план. Повторяем попытку…',
    answerFailed: 'Не удалось сохранить этот ответ. Попробуйте ещё раз.',
    failed: 'Мне не удалось завершить разбор присланного. Пожалуйста, запустите пересмотр ещё раз.',
    reading: 'Читаю присланное…',
    currencyFormat: 'Введите трёхбуквенный код валюты, например USD, GBP или EUR.',
    currencyFailed: 'Не удалось сохранить эту валюту.',
    planningCurrencyAria: 'Валюта плана',
    useCurrency: 'Использовать эту валюту',
    analyzing: 'Разбираю ваши вопросы по планированию…',
    introChoose: 'Эти вопросы помогают уточнить ваши цифры. Каждый ответ обновляет ту часть плана, которая от него зависит.',
    introFacts: 'Некоторые сведения, от которых зависит ваш план, нигде не указаны в присланном, и я не буду их додумывать.',
    statement: (n) => `Сведение ${n}:`,
    person: (n) => `Человек ${n}`,
    personInline: (n) => `человека ${n}`,
    nameFor: (who) => `Имя для ${who}`,
    saveNames: 'Сохранить имена',
    currencyCodeLabel: 'Код валюты',
    currencyPlaceholder: 'напр. USD',
    caseSensitive: 'Регистр важен: GBP — фунты, GBp — пенсы.',
    replyInChat: 'Ответить на этот вопрос в чате',
    needsFactUpdate: 'Чтобы закрыть этот вопрос, сначала нужно обновить сведения.',
    alsoWorking: 'Параллельно разбираю остальные ваши вопросы по планированию.',
    currencyQuestion: 'В какой валюте план должен показывать итоговые суммы?',
  },
  report: {
    complete: 'Разбор вашего случая',
    partial: 'Что удалось установить на данный момент',
    notComplete: 'Это не полный финансовый план. Открытые вопросы показаны, а не додуманы.',
  },
  generate: {
    signIn: 'Войдите, чтобы сформировать отчёт по плану.',
    unreachable: 'Сервис плана недоступен — попробуйте ещё раз.',
    couldNotStart: 'Не удалось запустить отчёт — попробуйте чуть позже.',
    button: 'Сформировать отчёт по моему плану',
    working: 'Работаю над вашим планом — рассчитываю каждый раздел и указываю, чего ещё не хватает каждому из них. Обычно это занимает минуту-две.',
    notProduced: 'Не удалось сформировать отчёт. Ничего не было додумано взамен — попробуйте ещё раз; если ошибка повторится, это наша ошибка, а не ваша.',
    questionsWaiting: (count, n) => `В ассистенте вас ${ruPlural(count, 'ждёт', 'ждут', 'ждут')} ${n} ${ruPlural(count, 'вопрос', 'вопроса', 'вопросов')} по вашему случаю — ответы открывают зависящие от них разделы.`,
  },
  agenda: {
    status: { awaiting: 'ждёт вас', open: 'ещё открыт', failed: 'исправим мы', answered: 'отвечен' },
    clarifyTitle: 'Уточните профиль',
    clarifyBody: 'Эти пункты взяты из вашего сохранённого профиля, а не из этого отчёта. Исправив их, вы улучшите каждый план и отчёт, который на него опирается.',
    agendaTitle: 'Ваша повестка',
    agendaLead: 'Что этот отчёт разобрал по вашей ситуации',
    openCount: (count, n) => ` — ${ruPlural(count, 'открытым остаётся', 'открытыми остаются', 'открытыми остаются')} ${n} ${ruPlural(count, 'пункт', 'пункта', 'пунктов')}.`,
    allAnswered: ' — на все пункты есть ответы.',
    mismatchTitle: 'Это расходится с вашим сохранённым профилем',
    mismatchLine: (described, profile) => `: вы указали ${described}; в профиле — ${profile}.`,
  },
  blocks: {
    questionN: (n) => `Вопрос ${n}`,
    sendNofM: (chosen, total) => `Отправить ${chosen} из ${total}`,
    answerAllFirst: 'Можно ответить на все вопросы, прежде чем отправить.',
    back: 'Назад',
    next: 'Далее',
    typeOwn: 'Или напишите свой ответ',
    stepOfTotal: (n, total) => `${n} из ${total}`,
    figuresSource: 'Откуда эти цифры',
    computedByEngine: 'рассчитано модулем планирования',
    answerAria: 'Ответ',
    yourAnswer: 'Ваш ответ',
    send: 'Отправить',
    pp: (n) => `${n} п. п.`,
  },
  chat: {
    noted: 'Принято.',
    couldNotRecord: 'Сейчас не получилось это сохранить.',
    couldNotRecordRetry: 'Сейчас не получилось это сохранить — попробуйте ещё раз.',
    replyingTo: 'Ответ на:',
    cancelReply: 'Отменить ответ',
  },
}

const zh: MyCIOChatPanelsCopy = {
  review: {
    intro: '基于你当前的数据对计划做一次全面复核——它会进入队列、在后台计算，并保存为新的计划版本。',
    queueButton: '将全面复核加入队列',
    queuing: '正在加入队列…',
    checking: '正在查询你的复核…',
    queued: '你更新后的复核已在队列中。',
    ahead: (_count, n) => `你前面还有 ${n} 个复核。`,
    estimatedStart: (eta) => `预计开始：${eta}。`,
    waitingWorker: '当前阶段：等待计算服务器。',
    provisionalTiming: '这个时间只是估计，可能会变。',
    stageProgress: (stage, done, total) => `${stage}——已完成 ${done}/${total} 个阶段。`,
    stageOngoing: (stage) => `${stage}…`,
    estimatedCompletion: (eta) => `预计完成：${eta}。`,
    workerUnresponsive: '计算服务器最近没有回报状态。需要有可用的服务器才能恢复；完成时间未知。',
    draftReady: '你的工作版复核已就绪。',
    draftReadySub: '已完成的分析可供查看。已保存的计划没有更新。',
    complete: '你的复核已完成。',
    savedVersioned: '更新后的计划已保存并生成新版本。',
    letterPending: '它的计划信仍在撰写中。',
    letterFailed: '计划已保存，但其计划信暂不可用。你仍可查看已保存的结果。',
    letterUnverified: '无法确认此版本计划信的状态；各项数字不受影响。',
    letterIncomplete: '计划信缺少一个或多个部分——计划页面列出了这些部分；各项数字不受影响。',
    noReference: '复核已结束，但其结果引用不可用。',
    needsInput: '你的复核暂时无法运行——需要你先回答一个问题。',
    needsInputSub: '请在对话或计划中作答，然后再次申请复核。',
    superseded: '你的数据有变——更新的复核涵盖了当前数字。',
    failed: '此次复核未能完成。',
    reason: (label) => `原因：${label}。`,
    cancelled: '此次复核已取消。',
    none: '尚未申请任何复核。',
    accessUnavailable: '无法访问此复核。请检查你的登录状态和访问权限。',
    etaAbout: (time) => `约 ${time}`,
    failureReasons: {
      internal_error: '我们这边出现内部错误',
      engine_error: '规划引擎返回了错误',
      engine_refused: '规划引擎无法接受这些数据',
      engine_busy: '规划引擎繁忙',
      engine_deadline: '计算超出了允许的时间',
      persistence_failed: '结果未能保存',
      lease_expired: '计算服务器停止了响应',
      cancelled: '复核已被取消',
      review_status_unavailable: '无法读取复核状态',
      review_access_unavailable: '无法访问此复核',
      other: '发生了意外错误',
    },
    stages: {
      load_snapshot: '正在载入你已冻结的数据',
      compute_review: '正在运行蒙特卡洛模拟',
      publish_plan: '正在保存新的计划版本',
      needs_input: '需要更多信息才能计算此计划',
      computing: '正在计算',
      working: '正在处理',
    },
    retrievalError: '复核已完成，但结果未能加载。正在重新交付，不会重新计算。',
    retryLoading: '重新加载',
    notUpdated: '已保存的计划没有更新。',
    notCompleted: '本次运行未完成',
    questionsYouCanAnswer: '你可以回答的问题',
    runNow: '立即运行',
    cancel: '取消',
    reviewAgain: '再次复核',
    dismiss: '关闭',
  },
  sections: {
    engagement_scope: '你的问题与本计划的范围',
    assumptions: '数字背后的假设',
    executive_summary: '一页摘要',
    current_condition: '你目前的状况',
    tax_position: '税务如何改变答案',
    swot: '重大风险与抗风险能力',
    recommendations_and_options: '你可以选择的方案',
    implementation_actions: '下一步做什么、由谁来做',
    monitoring_and_review: '本计划何时再次复核',
    professionals: '本计划还需要的其他专业人士',
    sections_not_provided: '未提供的部分',
    open_items: '待解决事项',
    executive_letter: '你的计划现状',
    overview: '概览',
    what_you_asked: '你的问题',
    situation: '你的情况',
    goals: '目标',
    personal_and_goals: '你与你的目标',
    cashflow_debt: '现金流与负债',
    retirement: '退休',
    education: '教育',
    protection: '保障',
    tax: '税务',
    investments: '投资',
    work_business: '工作与企业',
    estate: '遗产',
    dates_that_matter: '重要日期',
    risk: '风险与抗风险能力',
    tradeoffs: '您的选项',
    next_steps: '下一步',
    monitoring: '执行与跟踪',
    net_worth_statement: '净资产表',
    cash_flow_and_income: '现金流与收入',
    balance_sheet: '资产负债表',
    judgement_and_options: '判断与方案',
    actions_and_administration: '行动与管理',
    tax_and_sources: '税务与依据',
    cross_border_tax: '跨境税务',
    tax_evaluation: '税务评估',
  },
  caseQ: {
    requestFailed: '请求未能完成。请检查你的回答后重试。',
    refreshFailed: '暂时无法刷新你的计划。正在重试…',
    answerFailed: '该回答未能记录。请重试。',
    failed: '我没能完成对你所发内容的审阅。请重新进行复核。',
    reading: '正在阅读你发送的内容…',
    currencyFormat: '请输入三个字母的货币代码，例如 USD、GBP 或 EUR。',
    currencyFailed: '该货币未能记录。',
    planningCurrencyAria: '计划货币',
    useCurrency: '使用此货币',
    analyzing: '正在处理你的规划问题…',
    introChoose: '这些问题有助于厘清你的数字。每个回答都会更新计划中依赖它的部分。',
    introFacts: '你的计划所依赖的一些信息在你发送的内容中没有提到，我不会去猜测。',
    statement: (n) => `陈述 ${n}：`,
    person: (n) => `第 ${n} 人`,
    personInline: (n) => `第 ${n} 人`,
    nameFor: (who) => `${who}的姓名`,
    saveNames: '保存姓名',
    currencyCodeLabel: '货币代码',
    currencyPlaceholder: '如 USD',
    caseSensitive: '代码区分大小写：GBP 是英镑，GBp 是便士。',
    replyInChat: '在对话中回答这个问题',
    needsFactUpdate: '需要先更新一项信息，这个问题才能确定。',
    alsoWorking: '我也在处理你其余的规划问题。',
    currencyQuestion: '你的计划应以哪种货币计算总额？',
  },
  report: {
    complete: '你的个案复核',
    partial: '目前能够确定的内容',
    notComplete: '这不是一份完整的财务计划。待解决事项会直接列出，而不是去猜测。',
  },
  generate: {
    signIn: '请登录以生成你的计划报告。',
    unreachable: '无法连接计划服务——请重试。',
    couldNotStart: '无法启动报告——请稍后重试。',
    button: '生成我的计划报告',
    working: '正在处理你的计划——逐一计算每个部分，并注明每个部分还在等待什么。通常需要一两分钟。',
    notProduced: '报告未能生成。我们没有用任何猜测来代替——请重试；如果问题重复出现，那是我们需要修复的问题，而不是你的。',
    questionsWaiting: (_count, n) => `你的个案有 ${n} 个问题在助手中等待回答——回答后即可解锁依赖它们的部分。`,
  },
  agenda: {
    status: { awaiting: '等待你处理', open: '仍待解决', failed: '需由我们修复', answered: '已回答' },
    clarifyTitle: '完善你的资料',
    clarifyBody: '这些来自你保存的资料，而不是本报告。修正后，所有读取这些资料的计划和报告都会随之改进。',
    agendaTitle: '你的议程',
    agendaLead: '本报告针对你的情况处理了哪些事项',
    openCount: (_count, n) => `——仍有 ${n} 项待解决。`,
    allAnswered: '——所有事项均已回答。',
    mismatchTitle: '这与你保存的资料不一致',
    mismatchLine: (described, profile) => `：你描述的是 ${described}；你的资料显示为 ${profile}。`,
  },
  blocks: {
    questionN: (n) => `问题 ${n}`,
    sendNofM: (chosen, total) => `发送 ${chosen}/${total}`,
    answerAllFirst: '你可以全部回答后再发送。',
    back: '返回',
    next: '下一步',
    typeOwn: '或者输入你自己的回答',
    stepOfTotal: (n, total) => `${n} / ${total}`,
    figuresSource: '这些数字从何而来',
    computedByEngine: '由规划引擎计算',
    answerAria: '回答',
    yourAnswer: '你的回答',
    send: '发送',
    pp: (n) => `${n} 个百分点`,
  },
  chat: {
    noted: '已记下。',
    couldNotRecord: '暂时无法记录这一点。',
    couldNotRecordRetry: '暂时无法记录这一点——请重试。',
    replyingTo: '回复：',
    cancelReply: '取消回复',
  },
}

const ja: MyCIOChatPanelsCopy = {
  review: {
    intro: '現在の入力内容でプラン全体を見直します。見直しはキューに入り、バックグラウンドで計算され、新しいプランのバージョンとして保存されます。',
    queueButton: '全体の見直しをキューに入れる',
    queuing: 'キューに追加しています…',
    checking: '見直しの状況を確認しています…',
    queued: '更新した見直しはキューに入っています。',
    ahead: (_count, n) => `お客様の前に ${n} 件の見直しがあります。`,
    estimatedStart: (eta) => `開始予定：${eta}。`,
    waitingWorker: '現在の段階：計算サーバーの空きを待っています。',
    provisionalTiming: 'この時期は見込みで、変わる可能性があります。',
    stageProgress: (stage, done, total) => `${stage} — ${total} 段階中 ${done} 段階が完了。`,
    stageOngoing: (stage) => `${stage}…`,
    estimatedCompletion: (eta) => `完了予定：${eta}。`,
    workerUnresponsive: '計算サーバーからしばらく応答がありません。再開には空いているサーバーが必要で、完了時刻はわかりません。',
    draftReady: '作業版の見直しができました。',
    draftReadySub: '完了した分析をご覧いただけます。保存済みのプランは更新されていません。',
    complete: '見直しが完了しました。',
    savedVersioned: '更新したプランは新しいバージョンとして保存されました。',
    letterPending: 'レターはまだ作成中です。',
    letterFailed: 'プランは保存されましたが、レターは利用できません。保存された結果は引き続きご覧いただけます。',
    letterUnverified: 'このバージョンのレターの状態を確認できませんでした。数値はそのまま有効です。',
    letterIncomplete: 'レターに欠けているセクションがあります。プランのページに記載しています。数値はそのまま有効です。',
    noReference: '見直しは終了しましたが、結果の参照先を取得できません。',
    needsInput: '見直しはまだ実行できません。先にご回答いただく必要があります。',
    needsInputSub: 'チャットかプラン上でご回答のうえ、もう一度見直しを依頼してください。',
    superseded: '入力内容が変わりました。現在の数値は新しい見直しに反映されます。',
    failed: 'この見直しは完了できませんでした。',
    reason: (label) => `理由：${label}。`,
    cancelled: 'この見直しはキャンセルされました。',
    none: 'まだ見直しは依頼されていません。',
    accessUnavailable: 'この見直しにアクセスできません。サインイン状態とアクセス権をご確認ください。',
    etaAbout: (time) => `${time} ごろ`,
    failureReasons: {
      internal_error: '当社側の内部エラー',
      engine_error: 'プランニングエンジンがエラーを返しました',
      engine_refused: 'プランニングエンジンがこの入力を受け付けられませんでした',
      engine_busy: 'プランニングエンジンが混み合っていました',
      engine_deadline: '計算が制限時間を超えました',
      persistence_failed: '結果を保存できませんでした',
      lease_expired: '計算サーバーが応答しなくなりました',
      cancelled: '見直しがキャンセルされました',
      review_status_unavailable: '見直しの状態を読み取れませんでした',
      review_access_unavailable: 'この見直しにアクセスできませんでした',
      other: '予期しないエラー',
    },
    stages: {
      load_snapshot: '確定済みの入力内容を読み込んでいます',
      compute_review: 'モンテカルロ・シミュレーションを実行しています',
      publish_plan: '新しいプランのバージョンを保存しています',
      needs_input: 'このプランを計算するには追加の情報が必要です',
      computing: '計算中',
      working: '処理中',
    },
    retrievalError: '見直しは完了しましたが、結果を読み込めませんでした。計算をやり直さずに、結果の受け渡しを再試行しています。',
    retryLoading: '再読み込み',
    notUpdated: '保存済みのプランは更新されていません。',
    notCompleted: '今回の実行で完了しなかった項目',
    questionsYouCanAnswer: 'ご回答いただける質問',
    runNow: '今すぐ実行',
    cancel: 'キャンセル',
    reviewAgain: 'もう一度見直す',
    dismiss: '閉じる',
  },
  sections: {
    engagement_scope: 'ご質問と本プランの対象範囲',
    assumptions: '数値の前提となる仮定',
    executive_summary: '1ページの要約',
    current_condition: '現在の状況',
    tax_position: '税金が答えを左右するところ',
    swot: '重要なリスクと耐性',
    recommendations_and_options: '取りうる選択肢',
    implementation_actions: '次に何を、誰が行うか',
    monitoring_and_review: '本プランの次回見直し時期',
    professionals: '本プランに必要なほかの専門家',
    sections_not_provided: '未提出のセクション',
    open_items: '未解決の項目',
    executive_letter: '現在のプラン',
    overview: '概要',
    what_you_asked: 'ご質問',
    situation: 'お客様の状況',
    goals: '目標',
    personal_and_goals: 'お客様と目標',
    cashflow_debt: 'キャッシュフローと負債',
    retirement: '退職・老後',
    education: '教育',
    protection: '保障',
    tax: '税金',
    investments: '投資',
    work_business: '仕事と事業',
    estate: '相続',
    dates_that_matter: '重要な日付',
    risk: 'リスクと耐性',
    tradeoffs: 'あなたの選択肢',
    next_steps: '次にすること',
    monitoring: '実行とモニタリング',
    net_worth_statement: '純資産一覧',
    cash_flow_and_income: 'キャッシュフローと収入',
    balance_sheet: 'バランスシート',
    judgement_and_options: '判断と選択肢',
    actions_and_administration: '実行と手続き',
    tax_and_sources: '税金と出典',
    cross_border_tax: '国際税務',
    tax_evaluation: '税務評価',
  },
  caseQ: {
    requestFailed: 'リクエストを完了できませんでした。回答を確認して、もう一度お試しください。',
    refreshFailed: '現在プランを更新できません。再試行しています…',
    answerFailed: 'その回答を記録できませんでした。もう一度お試しください。',
    failed: 'お送りいただいた内容の確認を完了できませんでした。もう一度見直しをお試しください。',
    reading: 'お送りいただいた内容を読んでいます…',
    currencyFormat: 'USD、GBP、EUR などの3文字の通貨コードを入力してください。',
    currencyFailed: 'その通貨を記録できませんでした。',
    planningCurrencyAria: 'プランの通貨',
    useCurrency: 'この通貨を使う',
    analyzing: 'プランニング上のご質問を検討しています…',
    introChoose: 'これらの質問は数値を明確にするためのものです。回答ごとに、それに関わるプランの部分が更新されます。',
    introFacts: 'プランに必要な情報のうち、お送りいただいた内容のどこにも書かれていないものがあります。推測はいたしません。',
    statement: (n) => `記載 ${n}：`,
    person: (n) => `${n} 人目`,
    personInline: (n) => `${n} 人目`,
    nameFor: (who) => `${who}の名前`,
    saveNames: '名前を保存',
    currencyCodeLabel: '通貨コード',
    currencyPlaceholder: '例：USD',
    caseSensitive: 'コードは大文字・小文字を区別します。GBP はポンド、GBp はペンスです。',
    replyInChat: 'この質問にチャットで答える',
    needsFactUpdate: 'この質問を確定するには、先に情報の更新が必要です。',
    alsoWorking: '残りのプランニング上のご質問も検討しています。',
    currencyQuestion: 'プランの合計はどの通貨で表示しますか？',
  },
  report: {
    complete: 'ケースの見直し結果',
    partial: '現時点で確認できること',
    notComplete: 'これは完全な資産計画ではありません。未解決の項目は推測せずにそのまま示しています。',
  },
  generate: {
    signIn: 'プランのレポートを作成するにはサインインしてください。',
    unreachable: 'プランのサービスに接続できません。もう一度お試しください。',
    couldNotStart: 'レポートを開始できませんでした。しばらくしてからお試しください。',
    button: 'プランのレポートを作成',
    working: 'プランを検討しています。各セクションを計算し、まだ何を待っているかを明示します。通常1〜2分かかります。',
    notProduced: 'レポートを作成できませんでした。代わりに推測で埋めたものはありません。もう一度お試しください。繰り返し起こる場合は当社側の問題であり、お客様の問題ではありません。',
    questionsWaiting: (_count, n) => `ケースに関する ${n} 件の質問がアシスタントでご回答を待っています。回答すると、それに依存するセクションが進められます。`,
  },
  agenda: {
    status: { awaiting: 'ご回答待ち', open: '未解決', failed: '当社で対応', answered: '回答済み' },
    clarifyTitle: 'プロフィールの確認',
    clarifyBody: 'これらは保存済みのプロフィールに由来するもので、このレポートによるものではありません。修正すると、それを参照するすべてのプランとレポートが改善されます。',
    agendaTitle: 'アジェンダ',
    agendaLead: 'このレポートがお客様の状況について検討した項目',
    openCount: (_count, n) => ` — ${n} 件が未解決です。`,
    allAnswered: ' — すべての項目に回答済みです。',
    mismatchTitle: '保存済みのプロフィールと一致しません',
    mismatchLine: (described, profile) => `：ご説明の内容は ${described}、プロフィールでは ${profile} です。`,
  },
  blocks: {
    questionN: (n) => `質問 ${n}`,
    sendNofM: (chosen, total) => `${total} 件中 ${chosen} 件を送信`,
    answerAllFirst: 'すべてに回答してから送信できます。',
    back: '戻る',
    next: '次へ',
    typeOwn: 'または自分で回答を入力',
    stepOfTotal: (n, total) => `${n} / ${total}`,
    figuresSource: 'これらの数値の出どころ',
    computedByEngine: 'プランニングエンジンによる計算',
    answerAria: '回答',
    yourAnswer: 'ご回答',
    send: '送信',
    pp: (n) => `${n}ポイント`,
  },
  chat: {
    noted: '承知しました。',
    couldNotRecord: '今は記録できませんでした。',
    couldNotRecordRetry: '今は記録できませんでした。もう一度お試しください。',
    replyingTo: '返信先：',
    cancelReply: '返信をやめる',
  },
}

const ko: MyCIOChatPanelsCopy = {
  review: {
    intro: '현재 입력값으로 플랜 전체를 검토합니다. 검토는 대기열에 들어가 백그라운드에서 계산되고, 새 플랜 버전으로 저장됩니다.',
    queueButton: '전체 검토 대기열에 추가',
    queuing: '대기열에 추가하는 중…',
    checking: '검토 상태를 확인하는 중…',
    queued: '업데이트된 검토가 대기열에 있어요.',
    ahead: (_count, n) => `앞에 ${n}건의 검토가 있어요.`,
    estimatedStart: (eta) => `예상 시작: ${eta}.`,
    waitingWorker: '현재 단계: 계산 서버를 기다리는 중.',
    provisionalTiming: '이 시기는 추정치이며 바뀔 수 있습니다.',
    stageProgress: (stage, done, total) => `${stage} — ${total}단계 중 ${done}단계 완료.`,
    stageOngoing: (stage) => `${stage}…`,
    estimatedCompletion: (eta) => `예상 완료: ${eta}.`,
    workerUnresponsive: '계산 서버가 한동안 응답하지 않았어요. 재개하려면 사용 가능한 서버가 필요하며, 완료 시점은 알 수 없어요.',
    draftReady: '작업용 검토가 준비됐어요.',
    draftReadySub: '완료된 분석을 볼 수 있어요. 저장된 플랜은 업데이트되지 않았어요.',
    complete: '검토가 완료됐어요.',
    savedVersioned: '업데이트된 플랜이 새 버전으로 저장됐어요.',
    letterPending: '플랜 레터는 아직 작성 중이에요.',
    letterFailed: '플랜은 저장됐지만 레터는 사용할 수 없어요. 저장된 결과는 계속 볼 수 있어요.',
    letterUnverified: '이 버전의 레터 상태를 확인할 수 없었어요. 수치는 그대로 유효해요.',
    letterIncomplete: '레터에 하나 이상의 섹션이 빠져 있어요. 플랜 페이지에 표시돼 있으며, 수치는 그대로 유효해요.',
    noReference: '검토는 끝났지만 결과 참조를 가져올 수 없어요.',
    needsInput: '검토를 아직 실행할 수 없어요. 먼저 답변이 필요해요.',
    needsInputSub: '채팅이나 플랜에서 답한 뒤 검토를 다시 요청하세요.',
    superseded: '입력값이 바뀌었어요. 현재 수치는 더 최근의 검토에 반영돼요.',
    failed: '이 검토를 완료하지 못했어요.',
    reason: (label) => `사유: ${label}.`,
    cancelled: '이 검토는 취소됐어요.',
    none: '아직 요청된 검토가 없어요.',
    accessUnavailable: '이 검토에 접근할 수 없어요. 로그인 상태와 접근 권한을 확인하세요.',
    etaAbout: (time) => `${time}경`,
    failureReasons: {
      internal_error: '당사 측 내부 오류',
      engine_error: '플래닝 엔진이 오류를 반환함',
      engine_refused: '플래닝 엔진이 이 입력값을 받을 수 없음',
      engine_busy: '플래닝 엔진이 사용 중이었음',
      engine_deadline: '계산이 허용 시간을 초과함',
      persistence_failed: '결과를 저장하지 못함',
      lease_expired: '계산 서버가 응답을 멈춤',
      cancelled: '검토가 취소됨',
      review_status_unavailable: '검토 상태를 읽을 수 없음',
      review_access_unavailable: '이 검토에 접근할 수 없음',
      other: '예기치 않은 오류',
    },
    stages: {
      load_snapshot: '확정된 입력값을 불러오는 중',
      compute_review: '몬테카를로 시뮬레이션 실행 중',
      publish_plan: '새 플랜 버전을 저장하는 중',
      needs_input: '이 플랜을 계산하려면 추가 정보가 필요해요',
      computing: '계산 중',
      working: '처리 중',
    },
    retrievalError: '검토는 끝났지만 결과를 불러오지 못했어요. 계산을 다시 하지 않고 결과 전달을 재시도하고 있어요.',
    retryLoading: '다시 불러오기',
    notUpdated: '저장된 플랜은 업데이트되지 않았어요.',
    notCompleted: '이번 실행에서 완료되지 않음',
    questionsYouCanAnswer: '답할 수 있는 질문',
    runNow: '지금 실행',
    cancel: '취소',
    reviewAgain: '다시 검토',
    dismiss: '닫기',
  },
  sections: {
    engagement_scope: '질문하신 내용과 이 플랜의 범위',
    assumptions: '수치의 바탕이 된 가정',
    executive_summary: '한 페이지 요약',
    current_condition: '현재 상황',
    tax_position: '세금이 답을 바꾸는 지점',
    swot: '중대한 위험과 회복력',
    recommendations_and_options: '선택할 수 있는 옵션',
    implementation_actions: '다음 단계와 담당자',
    monitoring_and_review: '이 플랜의 다음 검토 시점',
    professionals: '이 플랜에 필요한 다른 전문가',
    sections_not_provided: '제공되지 않은 섹션',
    open_items: '미결 사항',
    executive_letter: '현재 플랜',
    overview: '개요',
    what_you_asked: '질문하신 내용',
    situation: '고객님의 상황',
    goals: '목표',
    personal_and_goals: '고객님과 목표',
    cashflow_debt: '현금흐름과 부채',
    retirement: '은퇴',
    education: '교육',
    protection: '보장',
    tax: '세금',
    investments: '투자',
    work_business: '일과 사업',
    estate: '상속',
    dates_that_matter: '중요한 날짜',
    risk: '위험과 회복력',
    tradeoffs: '선택할 수 있는 옵션',
    next_steps: '다음에 할 일',
    monitoring: '실행과 모니터링',
    net_worth_statement: '순자산표',
    cash_flow_and_income: '현금흐름과 소득',
    balance_sheet: '재무상태표',
    judgement_and_options: '판단과 옵션',
    actions_and_administration: '실행과 관리',
    tax_and_sources: '세금과 출처',
    cross_border_tax: '국제 세무',
    tax_evaluation: '세무 평가',
  },
  caseQ: {
    requestFailed: '요청을 완료하지 못했어요. 답변을 확인하고 다시 시도하세요.',
    refreshFailed: '지금은 플랜을 새로 고칠 수 없어요. 다시 시도하는 중…',
    answerFailed: '그 답변을 기록하지 못했어요. 다시 시도하세요.',
    failed: '보내주신 내용을 끝까지 검토하지 못했어요. 검토를 다시 시도하세요.',
    reading: '보내주신 내용을 읽는 중…',
    currencyFormat: 'USD, GBP, EUR처럼 세 글자 통화 코드를 입력하세요.',
    currencyFailed: '그 통화를 기록하지 못했어요.',
    planningCurrencyAria: '플랜 통화',
    useCurrency: '이 통화 사용',
    analyzing: '플래닝 질문을 검토하는 중…',
    introChoose: '이 질문들은 수치를 명확히 하는 데 도움이 돼요. 답할 때마다 그에 달린 플랜 부분이 업데이트돼요.',
    introFacts: '플랜에 필요한 몇 가지 정보가 보내주신 내용 어디에도 없어요. 추측하지는 않을게요.',
    statement: (n) => `진술 ${n}:`,
    person: (n) => `${n}번째 사람`,
    personInline: (n) => `${n}번째 사람`,
    nameFor: (who) => `${who}의 이름`,
    saveNames: '이름 저장',
    currencyCodeLabel: '통화 코드',
    currencyPlaceholder: '예: USD',
    caseSensitive: '코드는 대소문자를 구분해요. GBP는 파운드, GBp는 펜스예요.',
    replyInChat: '이 질문에 채팅으로 답하기',
    needsFactUpdate: '이 질문을 확정하려면 먼저 정보를 업데이트해야 해요.',
    alsoWorking: '나머지 플래닝 질문도 검토하고 있어요.',
    currencyQuestion: '플랜의 합계를 어떤 통화로 표시할까요?',
  },
  report: {
    complete: '케이스 검토 결과',
    partial: '지금까지 확인할 수 있는 내용',
    notComplete: '이것은 완전한 재무 플랜이 아니에요. 미결 사항은 추측하지 않고 그대로 보여 드려요.',
  },
  generate: {
    signIn: '플랜 보고서를 만들려면 로그인하세요.',
    unreachable: '플랜 서비스에 연결할 수 없어요. 다시 시도하세요.',
    couldNotStart: '보고서를 시작하지 못했어요. 잠시 후 다시 시도하세요.',
    button: '내 플랜 보고서 만들기',
    working: '플랜을 검토하는 중이에요. 각 섹션을 계산하고, 섹션마다 아직 무엇을 기다리는지 알려 드려요. 보통 1~2분 걸려요.',
    notProduced: '보고서를 만들지 못했어요. 대신 추측으로 채운 것은 없어요. 다시 시도해 주세요. 계속 반복된다면 고객님이 아니라 저희가 고쳐야 할 문제예요.',
    questionsWaiting: (_count, n) => `케이스 관련 질문 ${n}개가 어시스턴트에서 답변을 기다리고 있어요. 답하면 그에 달린 섹션이 진행돼요.`,
  },
  agenda: {
    status: { awaiting: '답변 대기', open: '미결', failed: '저희가 해결할 문제', answered: '답변 완료' },
    clarifyTitle: '프로필 확인',
    clarifyBody: '이 항목들은 이 보고서가 아니라 저장된 프로필에서 나온 거예요. 고치면 프로필을 사용하는 모든 플랜과 보고서가 좋아져요.',
    agendaTitle: '아젠다',
    agendaLead: '이 보고서가 고객님의 상황에 대해 다룬 항목',
    openCount: (_count, n) => ` — ${n}개 항목이 아직 미결이에요.`,
    allAnswered: ' — 모든 항목에 답변이 있어요.',
    mismatchTitle: '저장된 프로필과 일치하지 않아요',
    mismatchLine: (described, profile) => `: 설명하신 내용은 ${described}, 프로필에는 ${profile}(으)로 되어 있어요.`,
  },
  blocks: {
    questionN: (n) => `질문 ${n}`,
    sendNofM: (chosen, total) => `${total}개 중 ${chosen}개 보내기`,
    answerAllFirst: '모두 답한 뒤에 보낼 수 있어요.',
    back: '뒤로',
    next: '다음',
    typeOwn: '또는 직접 답을 입력하세요',
    stepOfTotal: (n, total) => `${n} / ${total}`,
    figuresSource: '이 수치의 출처',
    computedByEngine: '플래닝 엔진이 계산함',
    answerAria: '답변',
    yourAnswer: '답변 입력',
    send: '보내기',
    pp: (n) => `${n}%p`,
  },
  chat: {
    noted: '기록했어요.',
    couldNotRecord: '지금은 기록하지 못했어요.',
    couldNotRecordRetry: '지금은 기록하지 못했어요. 다시 시도하세요.',
    replyingTo: '답장 대상:',
    cancelReply: '답장 취소',
  },
}

const ar: MyCIOChatPanelsCopy = {
  review: {
    intro: 'أجرِ مراجعة كاملة لخطتك على بياناتك الحالية — تدخل قائمة الانتظار، وتُحسب في الخلفية، ثم تُحفظ نسخة جديدة من الخطة.',
    queueButton: 'إضافة مراجعة كاملة إلى قائمة الانتظار',
    queuing: 'جارٍ الإضافة إلى قائمة الانتظار…',
    checking: 'جارٍ التحقق من مراجعتك…',
    queued: 'مراجعتك المحدّثة في قائمة الانتظار.',
    ahead: (count, n) => (count === 1 ? 'أمامك مراجعة واحدة.' : count === 2 ? 'أمامك مراجعتان.'
      : count >= 3 && count <= 10 ? `أمامك ${n} مراجعات.` : `أمامك ${n} مراجعة.`),
    estimatedStart: (eta) => `البدء المتوقع: ${eta}.`,
    waitingWorker: 'المرحلة الحالية: في انتظار خادم الحساب.',
    provisionalTiming: 'هذا التوقيت تقدير وقد يتغير.',
    stageProgress: (stage, done, total) => `${stage} — اكتملت ${done} من ${total} مراحل.`,
    stageOngoing: (stage) => `${stage}…`,
    estimatedCompletion: (eta) => `الاكتمال المتوقع: ${eta}.`,
    workerUnresponsive: 'لم يرسل خادم الحساب أي تحديث مؤخرًا. يتطلب الاستئناف خادمًا متاحًا؛ وموعد الاكتمال غير معروف.',
    draftReady: 'النسخة العملية من مراجعتك جاهزة.',
    draftReadySub: 'التحليل المكتمل متاح. لم تُحدَّث الخطة المحفوظة.',
    complete: 'اكتملت مراجعتك.',
    savedVersioned: 'حُفظت الخطة المحدّثة كنسخة جديدة.',
    letterPending: 'ما زالت رسالتها قيد الكتابة.',
    letterFailed: 'حُفظت الخطة، لكن رسالتها غير متاحة. لا يزال بإمكانك الاطلاع على النتائج المحفوظة.',
    letterUnverified: 'تعذّر تأكيد حالة الرسالة لهذه النسخة؛ الأرقام تبقى كما هي.',
    letterIncomplete: 'تنقص رسالتها قسمًا أو أكثر — صفحة الخطة تذكرها؛ الأرقام تبقى كما هي.',
    noReference: 'انتهت المراجعة، لكن مرجع نتيجتها غير متاح.',
    needsInput: 'تعذّر تشغيل مراجعتك بعد — تحتاج أولًا إلى إجابة منك.',
    needsInputSub: 'أجب في المحادثة أو في خطتك، ثم اطلب المراجعة مجددًا.',
    superseded: 'تغيّرت بياناتك — مراجعة أحدث تغطي الأرقام الحالية.',
    failed: 'تعذّر إكمال هذه المراجعة.',
    reason: (label) => `السبب: ${label}.`,
    cancelled: 'أُلغيت هذه المراجعة.',
    none: 'لم تُطلب أي مراجعة بعد.',
    accessUnavailable: 'تعذّر الوصول إلى هذه المراجعة. تحقّق من تسجيل الدخول وصلاحيات الوصول.',
    etaAbout: (time) => `نحو ${time}`,
    failureReasons: {
      internal_error: 'خطأ داخلي من جهتنا',
      engine_error: 'أعاد محرّك التخطيط خطأً',
      engine_refused: 'تعذّر على محرّك التخطيط قبول هذه البيانات',
      engine_busy: 'كان محرّك التخطيط مشغولًا',
      engine_deadline: 'تجاوز الحساب الوقت المسموح',
      persistence_failed: 'تعذّر حفظ النتيجة',
      lease_expired: 'توقّف خادم الحساب عن الاستجابة',
      cancelled: 'أُلغيت المراجعة',
      review_status_unavailable: 'تعذّرت قراءة حالة المراجعة',
      review_access_unavailable: 'تعذّر الوصول إلى هذه المراجعة',
      other: 'خطأ غير متوقع',
    },
    stages: {
      load_snapshot: 'جارٍ تحميل بياناتك المثبّتة',
      compute_review: 'جارٍ تشغيل محاكاة مونت كارلو',
      publish_plan: 'جارٍ حفظ النسخة الجديدة من خطتك',
      needs_input: 'يلزم مزيد من المعلومات قبل أن يمكن حساب هذه الخطة',
      computing: 'جارٍ الحساب',
      working: 'جارٍ العمل',
    },
    retrievalError: 'انتهت مراجعتك، لكن تعذّر تحميل نتائجها. نعيد محاولة التسليم دون إعادة الحساب.',
    retryLoading: 'إعادة التحميل',
    notUpdated: 'لم تُحدَّث الخطة المحفوظة.',
    notCompleted: 'لم يكتمل في هذا التشغيل',
    questionsYouCanAnswer: 'أسئلة يمكنك الإجابة عنها',
    runNow: 'التشغيل الآن',
    cancel: 'إلغاء',
    reviewAgain: 'المراجعة مجددًا',
    dismiss: 'إغلاق',
  },
  sections: {
    engagement_scope: 'ما سألت عنه وما تغطيه هذه الخطة',
    assumptions: 'الافتراضات التي تقوم عليها الأرقام',
    executive_summary: 'ملخص في صفحة واحدة',
    current_condition: 'وضعك اليوم',
    tax_position: 'حيث تغيّر الضرائب الإجابة',
    swot: 'المخاطر الجوهرية والقدرة على الصمود',
    recommendations_and_options: 'الخيارات المتاحة لك',
    implementation_actions: 'الخطوات التالية ومن يتولاها',
    monitoring_and_review: 'متى تُراجَع هذه الخطة مجددًا',
    professionals: 'مختصون آخرون تحتاجهم هذه الخطة',
    sections_not_provided: 'أقسام لم تُقدَّم',
    open_items: 'بنود مفتوحة',
    executive_letter: 'خطتك الآن',
    overview: 'نظرة عامة',
    what_you_asked: 'ما سألت عنه',
    situation: 'وضعك',
    goals: 'الأهداف',
    personal_and_goals: 'أنت وأهدافك',
    cashflow_debt: 'التدفق النقدي والديون',
    retirement: 'التقاعد',
    education: 'التعليم',
    protection: 'الحماية التأمينية',
    tax: 'الضرائب',
    investments: 'الاستثمارات',
    work_business: 'العمل والنشاط التجاري',
    estate: 'التركة',
    dates_that_matter: 'تواريخ مهمة',
    risk: 'المخاطر والقدرة على الصمود',
    tradeoffs: 'خياراتك',
    next_steps: 'ما الذي تفعله بعد ذلك',
    monitoring: 'التنفيذ والمتابعة',
    net_worth_statement: 'بيان صافي الثروة',
    cash_flow_and_income: 'التدفق النقدي والدخل',
    balance_sheet: 'الميزانية الشخصية',
    judgement_and_options: 'التقدير والخيارات',
    actions_and_administration: 'الإجراءات والإدارة',
    tax_and_sources: 'الضرائب والمصادر',
    cross_border_tax: 'الضرائب العابرة للحدود',
    tax_evaluation: 'التقييم الضريبي',
  },
  caseQ: {
    requestFailed: 'تعذّر إتمام الطلب. تحقّق من إجابتك وحاول مجددًا.',
    refreshFailed: 'يتعذّر تحديث خطتك الآن. جارٍ إعادة المحاولة…',
    answerFailed: 'تعذّر تسجيل هذه الإجابة. يُرجى المحاولة مجددًا.',
    failed: 'لم أتمكن من إكمال مراجعة ما أرسلته. يُرجى إعادة المراجعة.',
    reading: 'جارٍ قراءة ما أرسلته…',
    currencyFormat: 'أدخل رمز عملة من ثلاثة أحرف، مثل USD أو GBP أو EUR.',
    currencyFailed: 'تعذّر تسجيل هذه العملة.',
    planningCurrencyAria: 'عملة الخطة',
    useCurrency: 'استخدام هذه العملة',
    analyzing: 'جارٍ العمل على أسئلة التخطيط الخاصة بك…',
    introChoose: 'تساعد هذه الأسئلة على توضيح أرقامك. كل إجابة تحدّث الجزء من خطتك الذي يعتمد عليها.',
    introFacts: 'بعض الأمور التي تعتمد عليها خطتك غير مذكورة في أي موضع مما أرسلته، ولن أخمّنها.',
    statement: (n) => `البيان ${n}:`,
    person: (n) => `الشخص ${n}`,
    personInline: (n) => `الشخص ${n}`,
    nameFor: (who) => `اسم ${who}`,
    saveNames: 'حفظ الأسماء',
    currencyCodeLabel: 'رمز العملة',
    currencyPlaceholder: 'مثل USD',
    caseSensitive: 'الرموز حساسة لحالة الأحرف: GBP تعني الجنيه، وGBp تعني البنس.',
    replyInChat: 'الرد على هذا السؤال في المحادثة',
    needsFactUpdate: 'يحتاج هذا السؤال إلى تحديث معلومة قبل أن يُحسم.',
    alsoWorking: 'أعمل أيضًا على بقية أسئلة التخطيط الخاصة بك.',
    currencyQuestion: 'بأي عملة ينبغي أن تعرض خطتك إجمالياتها؟',
  },
  report: {
    complete: 'مراجعة حالتك',
    partial: 'ما يمكنني تأكيده حتى الآن',
    notComplete: 'هذه ليست خطة مالية كاملة. البنود المفتوحة معروضة بدلًا من تخمينها.',
  },
  generate: {
    signIn: 'سجّل الدخول لإنشاء تقرير خطتك.',
    unreachable: 'تعذّر الوصول إلى خدمة الخطة — حاول مجددًا.',
    couldNotStart: 'تعذّر بدء التقرير — حاول مجددًا بعد قليل.',
    button: 'إنشاء تقرير خطتي',
    working: 'جارٍ العمل على خطتك — نحسب كل قسم ونوضح ما لا يزال كل قسم ينتظره. يستغرق ذلك عادة دقيقة أو دقيقتين.',
    notProduced: 'تعذّر إنشاء التقرير. لم يُخمَّن شيء مكانه — يُرجى المحاولة مجددًا، وإن تكرر ذلك فالخلل من جهتنا لا من جهتك.',
    questionsWaiting: (_count, n) => `عدد الأسئلة من حالتك التي تنتظرك في المساعد: ${n} — الإجابة عنها تفتح الأقسام التي تعتمد عليها.`,
  },
  agenda: {
    status: { awaiting: 'بانتظارك', open: 'لا يزال مفتوحًا', failed: 'علينا إصلاحه', answered: 'تمت الإجابة' },
    clarifyTitle: 'وضّح ملفك الشخصي',
    clarifyBody: 'هذه البنود مصدرها ملفك الشخصي المحفوظ، لا هذا التقرير. تصحيحها يحسّن كل خطة وتقرير يقرأه.',
    agendaTitle: 'جدول أعمالك',
    agendaLead: 'ما تناوله هذا التقرير بشأن وضعك',
    openCount: (_count, n) => ` — عدد البنود التي لا تزال مفتوحة: ${n}.`,
    allAnswered: ' — تمت الإجابة عن جميع البنود.',
    mismatchTitle: 'هذا لا يطابق ملفك الشخصي المحفوظ',
    mismatchLine: (described, profile) => `: وصفتَ ${described}؛ وملفك الشخصي يذكر ${profile}.`,
  },
  blocks: {
    questionN: (n) => `السؤال ${n}`,
    sendNofM: (chosen, total) => `إرسال ${chosen} من ${total}`,
    answerAllFirst: 'يمكنك الإجابة عنها كلها قبل الإرسال.',
    back: 'رجوع',
    next: 'التالي',
    typeOwn: 'أو اكتب إجابتك بنفسك',
    stepOfTotal: (n, total) => `${n} من ${total}`,
    figuresSource: 'من أين تأتي هذه الأرقام',
    computedByEngine: 'محسوب بواسطة محرّك التخطيط',
    answerAria: 'الإجابة',
    yourAnswer: 'إجابتك',
    send: 'إرسال',
    pp: (n) => `${n} نقطة مئوية`,
  },
  chat: {
    noted: 'تم التسجيل.',
    couldNotRecord: 'تعذّر عليّ تسجيل ذلك الآن.',
    couldNotRecordRetry: 'تعذّر عليّ تسجيل ذلك الآن — يُرجى المحاولة مجددًا.',
    replyingTo: 'ردًا على:',
    cancelReply: 'إلغاء الرد',
  },
}

const hi: MyCIOChatPanelsCopy = {
  review: {
    intro: 'अपने मौजूदा इनपुट पर अपनी योजना की पूरी समीक्षा चलाएँ — यह कतार में लगती है, पृष्ठभूमि में गणना होती है, और योजना का नया संस्करण सहेजा जाता है।',
    queueButton: 'पूरी समीक्षा कतार में लगाएँ',
    queuing: 'कतार में लगाया जा रहा है…',
    checking: 'आपकी समीक्षा की स्थिति देखी जा रही है…',
    queued: 'आपकी अद्यतन समीक्षा कतार में है।',
    ahead: (_count, n) => `आपसे पहले ${n} समीक्षाएँ कतार में हैं।`,
    estimatedStart: (eta) => `अनुमानित शुरुआत: ${eta}।`,
    waitingWorker: 'मौजूदा चरण: गणना सर्वर की प्रतीक्षा।',
    provisionalTiming: 'यह समय एक अनुमान है और बदल सकता है।',
    stageProgress: (stage, done, total) => `${stage} — ${total} में से ${done} चरण पूरे।`,
    stageOngoing: (stage) => `${stage}…`,
    estimatedCompletion: (eta) => `अनुमानित समापन: ${eta}।`,
    workerUnresponsive: 'गणना सर्वर ने हाल में कोई सूचना नहीं भेजी है। दोबारा शुरू होने के लिए एक उपलब्ध सर्वर चाहिए; पूरा होने का समय अज्ञात है।',
    draftReady: 'आपकी कार्यकारी समीक्षा तैयार है।',
    draftReadySub: 'पूरा विश्लेषण उपलब्ध है। सहेजी गई योजना अद्यतन नहीं हुई है।',
    complete: 'आपकी समीक्षा पूरी हो गई है।',
    savedVersioned: 'अद्यतन योजना नए संस्करण के रूप में सहेज दी गई है।',
    letterPending: 'इसका पत्र अभी लिखा जा रहा है।',
    letterFailed: 'योजना सहेज दी गई, पर उसका पत्र उपलब्ध नहीं है। आप सहेजे गए परिणाम अब भी देख सकते हैं।',
    letterUnverified: 'इस संस्करण के पत्र की स्थिति की पुष्टि नहीं हो सकी; आँकड़े यथावत हैं।',
    letterIncomplete: 'इसके पत्र में एक या अधिक खंड नहीं हैं — योजना पृष्ठ उनके नाम बताता है; आँकड़े यथावत हैं।',
    noReference: 'समीक्षा पूरी हुई, पर उसके परिणाम का संदर्भ उपलब्ध नहीं है।',
    needsInput: 'आपकी समीक्षा अभी नहीं चल सकी — पहले इसे आपके एक उत्तर की ज़रूरत है।',
    needsInputSub: 'चैट में या अपनी योजना में उत्तर दें, फिर समीक्षा दोबारा माँगें।',
    superseded: 'आपके इनपुट बदल गए हैं — मौजूदा आँकड़े एक नई समीक्षा में शामिल हैं।',
    failed: 'यह समीक्षा पूरी नहीं हो सकी।',
    reason: (label) => `कारण: ${label}।`,
    cancelled: 'यह समीक्षा रद्द कर दी गई।',
    none: 'अभी तक कोई समीक्षा नहीं माँगी गई है।',
    accessUnavailable: 'इस समीक्षा तक पहुँच नहीं हो पा रही। अपना साइन-इन और पहुँच अधिकार जाँचें।',
    etaAbout: (time) => `लगभग ${time}`,
    failureReasons: {
      internal_error: 'हमारी ओर की एक आंतरिक त्रुटि',
      engine_error: 'प्लानिंग इंजन ने त्रुटि लौटाई',
      engine_refused: 'प्लानिंग इंजन इन इनपुट को स्वीकार नहीं कर सका',
      engine_busy: 'प्लानिंग इंजन व्यस्त था',
      engine_deadline: 'गणना निर्धारित समय से अधिक चली',
      persistence_failed: 'परिणाम सहेजा नहीं जा सका',
      lease_expired: 'गणना सर्वर ने जवाब देना बंद कर दिया',
      cancelled: 'समीक्षा रद्द कर दी गई',
      review_status_unavailable: 'समीक्षा की स्थिति पढ़ी नहीं जा सकी',
      review_access_unavailable: 'इस समीक्षा तक पहुँच नहीं हो सकी',
      other: 'एक अप्रत्याशित त्रुटि',
    },
    stages: {
      load_snapshot: 'आपके स्थिर किए गए इनपुट लोड हो रहे हैं',
      compute_review: 'मोंटे कार्लो सिमुलेशन चल रहा है',
      publish_plan: 'आपकी योजना का नया संस्करण सहेजा जा रहा है',
      needs_input: 'इस योजना की गणना से पहले और जानकारी चाहिए',
      computing: 'गणना हो रही है',
      working: 'काम जारी है',
    },
    retrievalError: 'आपकी समीक्षा पूरी हो गई, पर उसके परिणाम लोड नहीं हो सके। गणना दोबारा चलाए बिना परिणाम फिर से भेजने की कोशिश की जा रही है।',
    retryLoading: 'फिर से लोड करें',
    notUpdated: 'सहेजी गई योजना अद्यतन नहीं हुई है।',
    notCompleted: 'इस बार पूरा नहीं हुआ',
    questionsYouCanAnswer: 'प्रश्न जिनका आप उत्तर दे सकते हैं',
    runNow: 'अभी चलाएँ',
    cancel: 'रद्द करें',
    reviewAgain: 'दोबारा समीक्षा करें',
    dismiss: 'बंद करें',
  },
  sections: {
    engagement_scope: 'आपने क्या पूछा और यह योजना किन बातों को कवर करती है',
    assumptions: 'आँकड़ों के पीछे की मान्यताएँ',
    executive_summary: 'एक पृष्ठ का सारांश',
    current_condition: 'आज आपकी स्थिति',
    tax_position: 'जहाँ कर उत्तर बदल देता है',
    swot: 'महत्वपूर्ण जोखिम और मज़बूती',
    recommendations_and_options: 'आपके सामने उपलब्ध विकल्प',
    implementation_actions: 'आगे क्या होगा, और कौन करेगा',
    monitoring_and_review: 'इस योजना की अगली समीक्षा कब होगी',
    professionals: 'इस योजना के लिए ज़रूरी अन्य पेशेवर',
    sections_not_provided: 'जो खंड नहीं दिए गए',
    open_items: 'खुले बिंदु',
    executive_letter: 'आपकी योजना अभी',
    overview: 'अवलोकन',
    what_you_asked: 'आपने क्या पूछा',
    situation: 'आपकी स्थिति',
    goals: 'लक्ष्य',
    personal_and_goals: 'आप और आपके लक्ष्य',
    cashflow_debt: 'नकदी प्रवाह और ऋण',
    retirement: 'सेवानिवृत्ति',
    education: 'शिक्षा',
    protection: 'सुरक्षा (बीमा)',
    tax: 'कर',
    investments: 'निवेश',
    work_business: 'काम और व्यवसाय',
    estate: 'संपत्ति उत्तराधिकार',
    dates_that_matter: 'महत्वपूर्ण तिथियाँ',
    risk: 'जोखिम और मज़बूती',
    tradeoffs: 'आपके विकल्प',
    next_steps: 'आगे क्या करें',
    monitoring: 'क्रियान्वयन और निगरानी',
    net_worth_statement: 'शुद्ध संपत्ति विवरण',
    cash_flow_and_income: 'नकदी प्रवाह और आय',
    balance_sheet: 'बैलेंस शीट',
    judgement_and_options: 'आकलन और विकल्प',
    actions_and_administration: 'कार्रवाई और प्रशासन',
    tax_and_sources: 'कर और स्रोत',
    cross_border_tax: 'सीमा-पार कराधान',
    tax_evaluation: 'कर मूल्यांकन',
  },
  caseQ: {
    requestFailed: 'अनुरोध पूरा नहीं हो सका। अपना उत्तर जाँचें और फिर कोशिश करें।',
    refreshFailed: 'अभी आपकी योजना रीफ़्रेश नहीं हो पा रही। फिर कोशिश की जा रही है…',
    answerFailed: 'यह उत्तर दर्ज नहीं हो सका। कृपया फिर कोशिश करें।',
    failed: 'आपने जो भेजा, उसकी समीक्षा मैं पूरी नहीं कर सका। कृपया समीक्षा फिर से चलाएँ।',
    reading: 'आपने जो भेजा, उसे पढ़ा जा रहा है…',
    currencyFormat: 'तीन अक्षरों वाला मुद्रा कोड दर्ज करें, जैसे USD, GBP या EUR।',
    currencyFailed: 'यह मुद्रा दर्ज नहीं हो सकी।',
    planningCurrencyAria: 'योजना की मुद्रा',
    useCurrency: 'यह मुद्रा इस्तेमाल करें',
    analyzing: 'आपके योजना संबंधी प्रश्नों पर काम हो रहा है…',
    introChoose: 'ये प्रश्न आपके आँकड़ों को स्पष्ट करने में मदद करते हैं। हर उत्तर योजना के उस हिस्से को अद्यतन करता है जो उस पर निर्भर है।',
    introFacts: 'आपकी योजना जिन कुछ बातों पर निर्भर है, वे आपके भेजे गए विवरण में कहीं नहीं हैं, और मैं उनका अनुमान नहीं लगाऊँगा।',
    statement: (n) => `कथन ${n}:`,
    person: (n) => `व्यक्ति ${n}`,
    personInline: (n) => `व्यक्ति ${n}`,
    nameFor: (who) => `${who} का नाम`,
    saveNames: 'नाम सहेजें',
    currencyCodeLabel: 'मुद्रा कोड',
    currencyPlaceholder: 'जैसे USD',
    caseSensitive: 'कोड में बड़े-छोटे अक्षर का फ़र्क मायने रखता है: GBP पाउंड है, GBp पेंस।',
    replyInChat: 'इस प्रश्न का उत्तर चैट में दें',
    needsFactUpdate: 'इस प्रश्न को तय करने से पहले एक जानकारी अद्यतन करनी होगी।',
    alsoWorking: 'मैं आपके बाकी योजना संबंधी प्रश्नों पर भी काम कर रहा हूँ।',
    currencyQuestion: 'आपकी योजना अपने कुल योग किस मुद्रा में दिखाए?',
  },
  report: {
    complete: 'आपके मामले की समीक्षा',
    partial: 'अब तक मैं क्या स्थापित कर पाया हूँ',
    notComplete: 'यह पूरी वित्तीय योजना नहीं है। खुले बिंदु अनुमान लगाने के बजाय दिखाए गए हैं।',
  },
  generate: {
    signIn: 'अपनी योजना रिपोर्ट बनाने के लिए साइन इन करें।',
    unreachable: 'योजना सेवा तक पहुँच नहीं हो पा रही — फिर कोशिश करें।',
    couldNotStart: 'रिपोर्ट शुरू नहीं हो सकी — थोड़ी देर में फिर कोशिश करें।',
    button: 'मेरी योजना रिपोर्ट बनाएँ',
    working: 'आपकी योजना पर काम हो रहा है — हर खंड की गणना की जा रही है, और बताया जा रहा है कि कौन-सा खंड अभी किस बात की प्रतीक्षा में है। आमतौर पर इसमें एक-दो मिनट लगते हैं।',
    notProduced: 'रिपोर्ट नहीं बन सकी। उसकी जगह कोई अनुमान नहीं भरा गया — कृपया फिर कोशिश करें; अगर ऐसा दोबारा हो, तो यह हमारी ओर की गड़बड़ी है, आपकी नहीं।',
    questionsWaiting: (_count, n) => `आपके मामले के ${n} प्रश्न सहायक में उत्तर की प्रतीक्षा कर रहे हैं — उनका उत्तर देने से उन पर निर्भर खंड आगे बढ़ते हैं।`,
  },
  agenda: {
    status: { awaiting: 'आपकी प्रतीक्षा में', open: 'अभी खुला', failed: 'हमें ठीक करना है', answered: 'उत्तर दिया गया' },
    clarifyTitle: 'अपनी प्रोफ़ाइल स्पष्ट करें',
    clarifyBody: 'ये बिंदु आपकी सहेजी गई प्रोफ़ाइल से आते हैं, इस रिपोर्ट से नहीं। इन्हें ठीक करने से हर वह योजना और रिपोर्ट बेहतर होती है जो इसे पढ़ती है।',
    agendaTitle: 'आपका एजेंडा',
    agendaLead: 'इस रिपोर्ट ने आपकी स्थिति के किन बिंदुओं पर काम किया',
    openCount: (_count, n) => ` — ${n} बिंदु अभी खुले हैं।`,
    allAnswered: ' — हर बिंदु का उत्तर दिया जा चुका है।',
    mismatchTitle: 'यह आपकी सहेजी गई प्रोफ़ाइल से मेल नहीं खाता',
    mismatchLine: (described, profile) => `: आपने ${described} बताया; आपकी प्रोफ़ाइल में ${profile} है।`,
  },
  blocks: {
    questionN: (n) => `प्रश्न ${n}`,
    sendNofM: (chosen, total) => `${total} में से ${chosen} भेजें`,
    answerAllFirst: 'भेजने से पहले आप सभी का उत्तर दे सकते हैं।',
    back: 'वापस',
    next: 'आगे',
    typeOwn: 'या अपना उत्तर लिखें',
    stepOfTotal: (n, total) => `${n} / ${total}`,
    figuresSource: 'ये आँकड़े कहाँ से आए',
    computedByEngine: 'प्लानिंग इंजन द्वारा गणना',
    answerAria: 'उत्तर',
    yourAnswer: 'आपका उत्तर',
    send: 'भेजें',
    pp: (n) => `${n} प्रतिशत अंक`,
  },
  chat: {
    noted: 'दर्ज कर लिया।',
    couldNotRecord: 'अभी मैं इसे दर्ज नहीं कर सका।',
    couldNotRecordRetry: 'अभी मैं इसे दर्ज नहीं कर सका — कृपया फिर कोशिश करें।',
    replyingTo: 'उत्तर दे रहे हैं:',
    cancelReply: 'उत्तर रद्द करें',
  },
}

const id: MyCIOChatPanelsCopy = {
  review: {
    intro: 'Jalankan tinjauan lengkap atas rencana Anda dengan data saat ini — tinjauan masuk antrean, dihitung di latar belakang, lalu disimpan sebagai versi rencana baru.',
    queueButton: 'Masukkan tinjauan lengkap ke antrean',
    queuing: 'Memasukkan ke antrean…',
    checking: 'Memeriksa tinjauan Anda…',
    queued: 'Tinjauan Anda yang diperbarui ada di antrean.',
    ahead: (_count, n) => `Ada ${n} tinjauan di depan Anda.`,
    estimatedStart: (eta) => `Perkiraan mulai: ${eta}.`,
    waitingWorker: 'Tahap saat ini: menunggu server perhitungan.',
    provisionalTiming: 'Waktu ini hanya perkiraan dan bisa berubah.',
    stageProgress: (stage, done, total) => `${stage} — ${done} dari ${total} tahap selesai.`,
    stageOngoing: (stage) => `${stage}…`,
    estimatedCompletion: (eta) => `Perkiraan selesai: ${eta}.`,
    workerUnresponsive: 'Server perhitungan belum melapor akhir-akhir ini. Pemulihan memerlukan server yang tersedia; waktu selesai belum diketahui.',
    draftReady: 'Tinjauan kerja Anda sudah siap.',
    draftReadySub: 'Analisis yang selesai tersedia. Rencana yang tersimpan belum diperbarui.',
    complete: 'Tinjauan Anda sudah selesai.',
    savedVersioned: 'Rencana yang diperbarui telah disimpan sebagai versi baru.',
    letterPending: 'Suratnya masih sedang ditulis.',
    letterFailed: 'Rencana sudah disimpan, tetapi suratnya tidak tersedia. Anda tetap dapat melihat hasil yang tersimpan.',
    letterUnverified: 'Status surat untuk versi ini tidak dapat dipastikan; angka-angkanya tetap berlaku.',
    letterIncomplete: 'Suratnya kekurangan satu bagian atau lebih — halaman rencana menyebutkannya; angka-angkanya tetap berlaku.',
    noReference: 'Tinjauan sudah selesai, tetapi referensi hasilnya tidak tersedia.',
    needsInput: 'Tinjauan Anda belum dapat berjalan — perlu jawaban dari Anda terlebih dahulu.',
    needsInputSub: 'Jawab di chat atau di rencana Anda, lalu minta tinjauan lagi.',
    superseded: 'Data Anda berubah — tinjauan yang lebih baru mencakup angka saat ini.',
    failed: 'Tinjauan ini tidak dapat diselesaikan.',
    reason: (label) => `Alasan: ${label}.`,
    cancelled: 'Tinjauan ini dibatalkan.',
    none: 'Belum ada tinjauan yang diminta.',
    accessUnavailable: 'Tinjauan ini tidak dapat diakses. Periksa status masuk dan hak akses Anda.',
    etaAbout: (time) => `sekitar ${time}`,
    failureReasons: {
      internal_error: 'kesalahan internal di pihak kami',
      engine_error: 'mesin perencanaan mengembalikan kesalahan',
      engine_refused: 'mesin perencanaan tidak dapat menerima data ini',
      engine_busy: 'mesin perencanaan sedang sibuk',
      engine_deadline: 'perhitungan melampaui batas waktu',
      persistence_failed: 'hasilnya tidak dapat disimpan',
      lease_expired: 'server perhitungan berhenti merespons',
      cancelled: 'tinjauan dibatalkan',
      review_status_unavailable: 'status tinjauan tidak dapat dibaca',
      review_access_unavailable: 'tinjauan ini tidak dapat diakses',
      other: 'kesalahan tak terduga',
    },
    stages: {
      load_snapshot: 'Memuat data Anda yang dibekukan',
      compute_review: 'Menjalankan simulasi Monte Carlo',
      publish_plan: 'Menyimpan versi rencana Anda yang baru',
      needs_input: 'Perlu informasi tambahan sebelum rencana ini dapat dihitung',
      computing: 'Menghitung',
      working: 'Memproses',
    },
    retrievalError: 'Tinjauan Anda sudah selesai, tetapi hasilnya tidak dapat dimuat. Mencoba mengirim ulang tanpa menjalankan perhitungan lagi.',
    retryLoading: 'Muat ulang',
    notUpdated: 'Rencana yang tersimpan belum diperbarui.',
    notCompleted: 'Belum selesai pada putaran ini',
    questionsYouCanAnswer: 'Pertanyaan yang dapat Anda jawab',
    runNow: 'Jalankan sekarang',
    cancel: 'Batal',
    reviewAgain: 'Tinjau lagi',
    dismiss: 'Tutup',
  },
  sections: {
    engagement_scope: 'Pertanyaan Anda dan cakupan rencana ini',
    assumptions: 'Asumsi di balik angka-angka',
    executive_summary: 'Ringkasan satu halaman',
    current_condition: 'Posisi Anda saat ini',
    tax_position: 'Di mana pajak mengubah jawabannya',
    swot: 'Risiko material dan ketahanan',
    recommendations_and_options: 'Pilihan yang terbuka bagi Anda',
    implementation_actions: 'Langkah berikutnya dan siapa yang menjalankannya',
    monitoring_and_review: 'Kapan rencana ini ditinjau lagi',
    professionals: 'Profesional lain yang dibutuhkan rencana ini',
    sections_not_provided: 'Bagian yang tidak disediakan',
    open_items: 'Hal yang masih terbuka',
    executive_letter: 'Rencana Anda saat ini',
    overview: 'Ikhtisar',
    what_you_asked: 'Pertanyaan Anda',
    situation: 'Situasi Anda',
    goals: 'Tujuan',
    personal_and_goals: 'Anda dan tujuan Anda',
    cashflow_debt: 'Arus kas & utang',
    retirement: 'Pensiun',
    education: 'Pendidikan',
    protection: 'Proteksi',
    tax: 'Pajak',
    investments: 'Investasi',
    work_business: 'Pekerjaan & usaha',
    estate: 'Warisan',
    dates_that_matter: 'Tanggal penting',
    risk: 'Risiko dan ketahanan',
    tradeoffs: 'Opsi Anda',
    next_steps: 'Yang perlu dilakukan berikutnya',
    monitoring: 'Pelaksanaan dan pemantauan',
    net_worth_statement: 'Laporan kekayaan bersih',
    cash_flow_and_income: 'Arus kas dan penghasilan',
    balance_sheet: 'Neraca pribadi',
    judgement_and_options: 'Penilaian dan pilihan',
    actions_and_administration: 'Tindakan dan administrasi',
    tax_and_sources: 'Pajak dan sumber',
    cross_border_tax: 'Pajak lintas negara',
    tax_evaluation: 'Evaluasi pajak',
  },
  caseQ: {
    requestFailed: 'Permintaan tidak dapat diselesaikan. Periksa jawaban Anda dan coba lagi.',
    refreshFailed: 'Rencana Anda tidak dapat dimuat ulang saat ini. Mencoba lagi…',
    answerFailed: 'Jawaban itu tidak dapat dicatat. Silakan coba lagi.',
    failed: 'Saya tidak dapat menyelesaikan peninjauan atas yang Anda kirim. Silakan jalankan tinjauan lagi.',
    reading: 'Membaca yang Anda kirim…',
    currencyFormat: 'Masukkan kode mata uang tiga huruf, seperti USD, GBP, atau EUR.',
    currencyFailed: 'Mata uang itu tidak dapat dicatat.',
    planningCurrencyAria: 'Mata uang rencana',
    useCurrency: 'Gunakan mata uang ini',
    analyzing: 'Mengerjakan pertanyaan perencanaan Anda…',
    introChoose: 'Pertanyaan ini membantu memperjelas angka Anda. Setiap jawaban memperbarui bagian rencana yang bergantung padanya.',
    introFacts: 'Beberapa hal yang menjadi dasar rencana Anda tidak disebutkan di mana pun dalam yang Anda kirim, dan saya tidak akan menebaknya.',
    statement: (n) => `Pernyataan ${n}:`,
    person: (n) => `Orang ${n}`,
    personInline: (n) => `orang ${n}`,
    nameFor: (who) => `Nama untuk ${who}`,
    saveNames: 'Simpan nama',
    currencyCodeLabel: 'Kode mata uang',
    currencyPlaceholder: 'mis. USD',
    caseSensitive: 'Kode membedakan huruf besar dan kecil: GBP adalah pound, GBp adalah pence.',
    replyInChat: 'Jawab pertanyaan ini di chat',
    needsFactUpdate: 'Pertanyaan ini memerlukan pembaruan data sebelum dapat diselesaikan.',
    alsoWorking: 'Saya juga sedang mengerjakan pertanyaan perencanaan Anda yang lain.',
    currencyQuestion: 'Mata uang apa yang harus dipakai rencana Anda untuk totalnya?',
  },
  report: {
    complete: 'Tinjauan kasus Anda',
    partial: 'Yang dapat saya pastikan sejauh ini',
    notComplete: 'Ini bukan rencana keuangan yang lengkap. Hal yang masih terbuka ditampilkan, bukan ditebak.',
  },
  generate: {
    signIn: 'Masuk untuk membuat laporan rencana Anda.',
    unreachable: 'Layanan rencana tidak dapat dijangkau — coba lagi.',
    couldNotStart: 'Laporan tidak dapat dimulai — coba lagi sebentar lagi.',
    button: 'Buat laporan rencana saya',
    working: 'Mengerjakan rencana Anda — menghitung setiap bagian dan menyebutkan apa yang masih ditunggu tiap bagian. Biasanya memakan waktu satu atau dua menit.',
    notProduced: 'Laporan tidak dapat dibuat. Tidak ada yang ditebak sebagai gantinya — silakan coba lagi; jika terulang, kesalahannya ada pada kami, bukan pada Anda.',
    questionsWaiting: (_count, n) => `${n} pertanyaan dari kasus Anda menunggu di asisten — menjawabnya membuka bagian yang bergantung padanya.`,
  },
  agenda: {
    status: { awaiting: 'menunggu Anda', open: 'masih terbuka', failed: 'kami yang memperbaiki', answered: 'terjawab' },
    clarifyTitle: 'Perjelas profil Anda',
    clarifyBody: 'Hal-hal ini berasal dari profil tersimpan Anda, bukan dari laporan ini. Memperbaikinya meningkatkan setiap rencana dan laporan yang membacanya.',
    agendaTitle: 'Agenda Anda',
    agendaLead: 'Yang dikerjakan laporan ini tentang situasi Anda',
    openCount: (_count, n) => ` — ${n} butir masih terbuka.`,
    allAnswered: ' — semua butir sudah terjawab.',
    mismatchTitle: 'Ini tidak cocok dengan profil tersimpan Anda',
    mismatchLine: (described, profile) => `: Anda menyebutkan ${described}; profil Anda menyatakan ${profile}.`,
  },
  blocks: {
    questionN: (n) => `Pertanyaan ${n}`,
    sendNofM: (chosen, total) => `Kirim ${chosen} dari ${total}`,
    answerAllFirst: 'Anda dapat menjawab semuanya sebelum mengirim.',
    back: 'Kembali',
    next: 'Lanjut',
    typeOwn: 'Atau ketik jawaban Anda sendiri',
    stepOfTotal: (n, total) => `${n} dari ${total}`,
    figuresSource: 'Dari mana angka-angka ini berasal',
    computedByEngine: 'dihitung oleh mesin perencanaan',
    answerAria: 'Jawaban',
    yourAnswer: 'Jawaban Anda',
    send: 'Kirim',
    pp: (n) => `${n} poin persentase`,
  },
  chat: {
    noted: 'Dicatat.',
    couldNotRecord: 'Saya belum dapat mencatatnya saat ini.',
    couldNotRecordRetry: 'Saya belum dapat mencatatnya saat ini — silakan coba lagi.',
    replyingTo: 'Membalas:',
    cancelReply: 'Batalkan balasan',
  },
}

const tr: MyCIOChatPanelsCopy = {
  review: {
    intro: 'Planınızı mevcut verilerinizle baştan sona gözden geçirin — inceleme sıraya alınır, arka planda hesaplanır ve yeni bir plan sürümü olarak kaydedilir.',
    queueButton: 'Tam incelemeyi sıraya al',
    queuing: 'Sıraya alınıyor…',
    checking: 'İncelemeniz kontrol ediliyor…',
    queued: 'Güncellenmiş incelemeniz sırada.',
    ahead: (_count, n) => `Sizden önce ${n} inceleme var.`,
    estimatedStart: (eta) => `Tahmini başlangıç: ${eta}.`,
    waitingWorker: 'Mevcut aşama: bir hesaplama sunucusu bekleniyor.',
    provisionalTiming: 'Bu zamanlama bir tahmindir ve değişebilir.',
    stageProgress: (stage, done, total) => `${stage} — ${total} aşamadan ${done} tanesi tamamlandı.`,
    stageOngoing: (stage) => `${stage}…`,
    estimatedCompletion: (eta) => `Tahmini tamamlanma: ${eta}.`,
    workerUnresponsive: 'Hesaplama sunucusu bir süredir durum bildirmedi. Devam etmesi için müsait bir sunucu gerekiyor; tamamlanma zamanı bilinmiyor.',
    draftReady: 'Çalışma incelemeniz hazır.',
    draftReadySub: 'Tamamlanan analiz görüntülenebilir. Kayıtlı plan güncellenmedi.',
    complete: 'İncelemeniz tamamlandı.',
    savedVersioned: 'Güncellenen plan yeni bir sürüm olarak kaydedildi.',
    letterPending: 'Plan mektubu hâlâ yazılıyor.',
    letterFailed: 'Plan kaydedildi ancak mektubu kullanılamıyor. Kayıtlı sonuçları yine de görebilirsiniz.',
    letterUnverified: 'Bu sürümün mektup durumu doğrulanamadı; rakamlar geçerliliğini koruyor.',
    letterIncomplete: 'Mektupta bir veya daha fazla bölüm eksik — plan sayfası bunları belirtiyor; rakamlar geçerliliğini koruyor.',
    noReference: 'İnceleme bitti ancak sonucunun referansı kullanılamıyor.',
    needsInput: 'İncelemeniz henüz çalışamadı — önce sizden bir yanıt gerekiyor.',
    needsInputSub: 'Sohbette veya planınızda yanıtlayın, ardından incelemeyi yeniden isteyin.',
    superseded: 'Verileriniz değişti — güncel rakamları daha yeni bir inceleme kapsıyor.',
    failed: 'Bu inceleme tamamlanamadı.',
    reason: (label) => `Neden: ${label}.`,
    cancelled: 'Bu inceleme iptal edildi.',
    none: 'Henüz bir inceleme istenmedi.',
    accessUnavailable: 'Bu incelemeye erişilemiyor. Oturumunuzu ve erişim izinlerinizi kontrol edin.',
    etaAbout: (time) => `yaklaşık ${time}`,
    failureReasons: {
      internal_error: 'bizim tarafımızda bir iç hata',
      engine_error: 'planlama motoru bir hata döndürdü',
      engine_refused: 'planlama motoru bu verileri kabul edemedi',
      engine_busy: 'planlama motoru meşguldü',
      engine_deadline: 'hesaplama süre sınırını aştı',
      persistence_failed: 'sonuç kaydedilemedi',
      lease_expired: 'hesaplama sunucusu yanıt vermeyi bıraktı',
      cancelled: 'inceleme iptal edildi',
      review_status_unavailable: 'inceleme durumu okunamadı',
      review_access_unavailable: 'bu incelemeye erişilemedi',
      other: 'beklenmeyen bir hata',
    },
    stages: {
      load_snapshot: 'Sabitlenen verileriniz yükleniyor',
      compute_review: 'Monte Carlo simülasyonu çalışıyor',
      publish_plan: 'Planınızın yeni sürümü kaydediliyor',
      needs_input: 'Bu planın hesaplanabilmesi için daha fazla bilgi gerekiyor',
      computing: 'Hesaplanıyor',
      working: 'İşleniyor',
    },
    retrievalError: 'İncelemeniz bitti ancak sonuçları yüklenemedi. Hesaplama yeniden çalıştırılmadan teslim tekrar deneniyor.',
    retryLoading: 'Yeniden yükle',
    notUpdated: 'Kayıtlı plan güncellenmedi.',
    notCompleted: 'Bu çalıştırmada tamamlanmadı',
    questionsYouCanAnswer: 'Yanıtlayabileceğiniz sorular',
    runNow: 'Şimdi çalıştır',
    cancel: 'İptal',
    reviewAgain: 'Yeniden incele',
    dismiss: 'Kapat',
  },
  sections: {
    engagement_scope: 'Sorduğunuz ve bu planın kapsamı',
    assumptions: 'Rakamların arkasındaki varsayımlar',
    executive_summary: 'Tek sayfalık özet',
    current_condition: 'Bugünkü durumunuz',
    tax_position: 'Verginin yanıtı değiştirdiği yer',
    swot: 'Önemli riskler ve dayanıklılık',
    recommendations_and_options: 'Önünüzdeki seçenekler',
    implementation_actions: 'Sonraki adımlar ve bunları kimin yapacağı',
    monitoring_and_review: 'Bu planın ne zaman yeniden inceleneceği',
    professionals: 'Bu planın ihtiyaç duyduğu diğer uzmanlar',
    sections_not_provided: 'Sağlanmayan bölümler',
    open_items: 'Açık konular',
    executive_letter: 'Planınız şu an',
    overview: 'Genel bakış',
    what_you_asked: 'Sorduğunuz',
    situation: 'Durumunuz',
    goals: 'Hedefler',
    personal_and_goals: 'Siz ve hedefleriniz',
    cashflow_debt: 'Nakit akışı ve borç',
    retirement: 'Emeklilik',
    education: 'Eğitim',
    protection: 'Koruma',
    tax: 'Vergi',
    investments: 'Yatırımlar',
    work_business: 'İş ve şirket',
    estate: 'Miras',
    dates_that_matter: 'Önemli tarihler',
    risk: 'Riskler ve dayanıklılık',
    tradeoffs: 'Seçenekleriniz',
    next_steps: 'Sırada ne var',
    monitoring: 'Uygulama ve izleme',
    net_worth_statement: 'Net varlık tablosu',
    cash_flow_and_income: 'Nakit akışı ve gelir',
    balance_sheet: 'Kişisel bilanço',
    judgement_and_options: 'Değerlendirme ve seçenekler',
    actions_and_administration: 'Eylemler ve işlemler',
    tax_and_sources: 'Vergi ve kaynaklar',
    cross_border_tax: 'Sınır ötesi vergilendirme',
    tax_evaluation: 'Vergi değerlendirmesi',
  },
  caseQ: {
    requestFailed: 'İstek tamamlanamadı. Yanıtınızı kontrol edip tekrar deneyin.',
    refreshFailed: 'Planınız şu anda yenilenemiyor. Tekrar deneniyor…',
    answerFailed: 'Bu yanıt kaydedilemedi. Lütfen tekrar deneyin.',
    failed: 'Gönderdiklerinizi incelemeyi bitiremedim. Lütfen incelemeyi yeniden deneyin.',
    reading: 'Gönderdikleriniz okunuyor…',
    currencyFormat: 'USD, GBP veya EUR gibi üç harfli bir para birimi kodu girin.',
    currencyFailed: 'Bu para birimi kaydedilemedi.',
    planningCurrencyAria: 'Plan para birimi',
    useCurrency: 'Bu para birimini kullan',
    analyzing: 'Planlama sorularınız üzerinde çalışılıyor…',
    introChoose: 'Bu sorular rakamlarınızı netleştirmeye yardımcı olur. Her yanıt, planınızın ona bağlı bölümünü günceller.',
    introFacts: 'Planınızın dayandığı bazı bilgiler gönderdiklerinizin hiçbir yerinde yer almıyor; bunları tahmin etmeyeceğim.',
    statement: (n) => `Beyan ${n}:`,
    person: (n) => `Kişi ${n}`,
    personInline: (n) => `kişi ${n}`,
    nameFor: (who) => `${who} için ad`,
    saveNames: 'Adları kaydet',
    currencyCodeLabel: 'Para birimi kodu',
    currencyPlaceholder: 'örn. USD',
    caseSensitive: 'Kodlar büyük/küçük harfe duyarlıdır: GBP sterlin, GBp peni demektir.',
    replyInChat: 'Bu soruyu sohbette yanıtla',
    needsFactUpdate: 'Bu sorunun netleşmesi için önce bir bilginin güncellenmesi gerekiyor.',
    alsoWorking: 'Diğer planlama sorularınız üzerinde de çalışıyorum.',
    currencyQuestion: 'Planınız toplamlarını hangi para biriminde göstersin?',
  },
  report: {
    complete: 'Dosyanızın incelemesi',
    partial: 'Şu ana kadar netleştirebildiklerim',
    notComplete: 'Bu, eksiksiz bir finansal plan değildir. Açık konular tahmin edilmek yerine gösterilir.',
  },
  generate: {
    signIn: 'Plan raporunuzu oluşturmak için giriş yapın.',
    unreachable: 'Plan hizmetine ulaşılamıyor — tekrar deneyin.',
    couldNotStart: 'Rapor başlatılamadı — biraz sonra tekrar deneyin.',
    button: 'Plan raporumu oluştur',
    working: 'Planınız üzerinde çalışılıyor — her bölüm hesaplanıyor ve hangi bölümün neyi beklediği belirtiliyor. Bu genellikle bir iki dakika sürer.',
    notProduced: 'Rapor oluşturulamadı. Yerine hiçbir şey tahmin edilmedi — lütfen tekrar deneyin; tekrarlarsa hata sizde değil, bizde.',
    questionsWaiting: (_count, n) => `Dosyanıza ait ${n} soru asistanda yanıt bekliyor — yanıtlamak, onlara bağlı bölümlerin önünü açar.`,
  },
  agenda: {
    status: { awaiting: 'sizi bekliyor', open: 'hâlâ açık', failed: 'bizim düzeltmemiz gerek', answered: 'yanıtlandı' },
    clarifyTitle: 'Profilinizi netleştirin',
    clarifyBody: 'Bunlar bu rapordan değil, kayıtlı profilinizden geliyor. Düzeltmek, profili kullanan her planı ve raporu iyileştirir.',
    agendaTitle: 'Gündeminiz',
    agendaLead: 'Bu raporun durumunuzla ilgili ele aldıkları',
    openCount: (_count, n) => ` — ${n} madde hâlâ açık.`,
    allAnswered: ' — tüm maddeler yanıtlandı.',
    mismatchTitle: 'Bu, kayıtlı profilinizle uyuşmuyor',
    mismatchLine: (described, profile) => `: ${described} olarak anlattınız; profilinizde ${profile} yazıyor.`,
  },
  blocks: {
    questionN: (n) => `Soru ${n}`,
    sendNofM: (chosen, total) => `${total} sorudan ${chosen} tanesini gönder`,
    answerAllFirst: 'Göndermeden önce hepsini yanıtlayabilirsiniz.',
    back: 'Geri',
    next: 'İleri',
    typeOwn: 'Veya kendi yanıtınızı yazın',
    stepOfTotal: (n, total) => `${n} / ${total}`,
    figuresSource: 'Bu rakamlar nereden geliyor',
    computedByEngine: 'planlama motoru tarafından hesaplandı',
    answerAria: 'Yanıt',
    yourAnswer: 'Yanıtınız',
    send: 'Gönder',
    pp: (n) => `${n} puan`,
  },
  chat: {
    noted: 'Not edildi.',
    couldNotRecord: 'Bunu şu anda kaydedemedim.',
    couldNotRecordRetry: 'Bunu şu anda kaydedemedim — lütfen tekrar deneyin.',
    replyingTo: 'Yanıtlanan:',
    cancelReply: 'Yanıtı iptal et',
  },
}

const byLanguage: Partial<Record<MyCIOLanguageCode, MyCIOChatPanelsCopy>> = {
  en, es, pt, fr, de, it, ru, zh, ja, ko, ar, hi, id, tr,
}

export function getMyCIOChatPanelsCopy(language: string | null | undefined): MyCIOChatPanelsCopy {
  const code = getMyCIOLanguage(language).code
  return pickCopy(code, byLanguage, en)
}

/** A section heading in the member's language; an unknown key falls back to its spaced form. */
export function sectionLabel(copy: MyCIOChatPanelsCopy, key: string): string {
  return (copy.sections as Record<string, string>)[key] ?? key.replaceAll('_', ' ')
}

/** An `unavailable_sections` entry (an English required-section title, or a key) in the member's language. */
export function unavailableSectionLabel(copy: MyCIOChatPanelsCopy, titleOrKey: string): string {
  const key = REQUIRED_SECTION_TITLES_EN[titleOrKey] ?? titleOrKey
  return (copy.sections as Record<string, string>)[key] ?? titleOrKey
}
